import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { extractExpenseReceipt, extractReceipt, mapReceiptToExpense } from '../utils/receiptExtraction';
import UserDialog from '../components/UserDialog';
import OrganizationLocations from '../components/OrganizationLocations';
import OrganizationCatalog from '../components/OrganizationCatalog';
import WebsiteChatbotSettings from '../components/WebsiteChatbotSettings';
import ExaminerManagement from '../components/examiner/ExaminerManagement';
import useExaminerVisibility from '../utils/useExaminerVisibility';
import OrganizationWebsite from '../components/OrganizationWebsite';
import Forms from './Forms';
import ActionIconButton from '../components/ActionIconButton';
import FeatureRequestAttachmentViewer from '../components/FeatureRequestAttachmentViewer';
import ReceiptExtractionStatus from '../components/ReceiptExtractionStatus';
import {
  getFeatureRequestCommentAttachmentUrl,
  getFeatureRequestDescriptionPreview,
  hasFeatureRequestCommentAttachment,
  resolveFeatureRequestCommentAttachmentUrl
} from '../utils/featureRequestUtils';

const HtmlRichEditor = ({ value, onChange, minHeight = '120px', placeholder = 'Enter content...', fixedHeight = false }) => {
  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState('');

  const computeIsEmpty = (htmlValue) => {
    const textOnly = (htmlValue || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return textOnly.length === 0;
  };

  useEffect(() => {
    if (!editorRef.current) {
      return;
    }

    const normalizedValue = value || '';
    if (editorRef.current.innerHTML !== normalizedValue) {
      editorRef.current.innerHTML = normalizedValue;
    }
    setIsEmpty(computeIsEmpty(normalizedValue));
  }, [value]);

  const handleInput = () => {
    if (!editorRef.current) {
      return;
    }
    const nextHtml = editorRef.current.innerHTML;
    setIsEmpty(computeIsEmpty(nextHtml));
    onChange(nextHtml);
  };

  const focusEditor = () => {
    if (editorRef.current) {
      editorRef.current.focus();
    }
  };

  const saveEditorSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || !editorRef.current) {
      return;
    }
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) {
      savedSelectionRef.current = range.cloneRange();
    }
  };

  const restoreEditorSelection = () => {
    if (!savedSelectionRef.current) {
      return false;
    }
    const selection = window.getSelection();
    if (!selection) {
      return false;
    }
    selection.removeAllRanges();
    selection.addRange(savedSelectionRef.current);
    return true;
  };

  const runCommand = (command, commandValue = null) => {
    focusEditor();
    document.execCommand(command, false, commandValue);
    handleInput();
  };

  const escapeHtml = (text) => (text || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const applyLink = () => {
    saveEditorSelection();
    const selection = window.getSelection();
    setLinkText(selection ? selection.toString() : '');
    setLinkUrl('');
    setLinkError('');
    setShowLinkDialog(true);
  };

  const closeLinkDialog = () => {
    setShowLinkDialog(false);
    setLinkText('');
    setLinkUrl('');
    setLinkError('');
    savedSelectionRef.current = null;
  };

  const insertLink = () => {
    const trimmedText = linkText.trim();
    const trimmedUrl = linkUrl.trim();
    if (!trimmedText || !trimmedUrl) {
      setLinkError('Link text and URL are required');
      return;
    }

    const href = /^[a-z][a-z0-9+.-]*:/i.test(trimmedUrl) ? trimmedUrl : `https://${trimmedUrl}`;
    focusEditor();
    restoreEditorSelection();
    document.execCommand('insertHTML', false, `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">${escapeHtml(trimmedText)}</a>`);
    handleInput();
    closeLinkDialog();
  };

  return (
    <div style={{ width: '100%', minWidth: 0 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.5rem' }}>
        <button type="button" title="Bold" aria-label="Bold" onClick={() => runCommand('bold')} style={{ fontSize: '0.8rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}><strong>B</strong></button>
        <button type="button" title="Italic" aria-label="Italic" onClick={() => runCommand('italic')} style={{ fontSize: '0.8rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}><em>I</em></button>
        <button type="button" title="Underline" aria-label="Underline" onClick={() => runCommand('underline')} style={{ fontSize: '0.8rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}><u>U</u></button>
        <button type="button" title="Paragraph" aria-label="Paragraph" onClick={() => runCommand('formatBlock', '<p>')} style={{ fontSize: '0.95rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}>¶</button>
        <button type="button" title="Bulleted list" aria-label="Bulleted list" onClick={() => runCommand('insertUnorderedList')} style={{ fontSize: '0.9rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}>•≡</button>
        <button type="button" title="Numbered list" aria-label="Numbered list" onClick={() => runCommand('insertOrderedList')} style={{ fontSize: '0.9rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}>1≡</button>
        <button type="button" title="Insert line break" aria-label="Insert line break" onClick={() => runCommand('insertHTML', '<br />')} style={{ fontSize: '1rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}>↵</button>
        <button type="button" title="Insert link" aria-label="Insert link" onMouseDown={(e) => e.preventDefault()} onClick={applyLink} style={{ fontSize: '0.95rem', padding: '0.2rem 0.45rem', minWidth: '32px', border: '1px solid #ced4da', borderRadius: '4px', backgroundColor: '#f8f9fa', cursor: 'pointer' }}>🔗</button>
      </div>
      <div style={{ position: 'relative', width: '100%', minWidth: 0 }}>
        {isEmpty && (
          <div style={{ position: 'absolute', top: '0.5rem', left: '0.5rem', color: '#6c757d', pointerEvents: 'none', fontSize: '0.9rem' }}>
            {placeholder}
          </div>
        )}
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onBlur={saveEditorSelection}
          onKeyUp={saveEditorSelection}
          onMouseUp={saveEditorSelection}
          style={{
            width: '100%',
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            minHeight,
            height: fixedHeight ? minHeight : undefined,
            maxHeight: fixedHeight ? minHeight : undefined,
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            overflowY: 'auto',
            overflowX: 'hidden',
            boxSizing: 'border-box'
          }}
        />
      </div>
      {showLinkDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', width: '420px', maxWidth: '90vw', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', padding: '1.25rem' }}>
            <h3 style={{ margin: '0 0 1rem 0', textAlign: 'center' }}>Insert Link</h3>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500 }}>Text to display:</label>
              <input type="text" value={linkText} onChange={(e) => setLinkText(e.target.value)} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }} autoFocus />
            </div>
            <div style={{ marginBottom: '0.75rem' }}>
              <label style={{ display: 'block', marginBottom: '0.35rem', fontWeight: 500 }}>URL:</label>
              <input type="url" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') insertLink(); }} placeholder="https://example.com" style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', boxSizing: 'border-box' }} />
            </div>
            {linkError && <div style={{ color: '#dc3545', marginBottom: '0.75rem', textAlign: 'center', fontSize: '0.9rem' }}>{linkError}</div>}
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button type="button" onClick={insertLink} style={{ padding: '0.45rem 0.9rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Insert</button>
              <button type="button" onClick={closeLinkDialog} style={{ padding: '0.45rem 0.9rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Manage = () => {
  const showExaminer = useExaminerVisibility();
  const { currentUser, setCurrentUser, people, aircraft, settings, setSettings, fetchManeuvers: fetchSharedManeuvers } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(() => location.pathname.startsWith('/manage/forms') ? 'forms' : currentUser?.user?.orgRole === 'instructor' ? 'examiner' : 'lessons');
  const isFormsRoute = location.pathname.startsWith('/manage/forms');
  useEffect(() => {
    if (isFormsRoute && activeTab !== 'forms') setActiveTab('forms');
    else if (!isFormsRoute && activeTab === 'forms') setActiveTab('announcements');
  }, [isFormsRoute, activeTab]);
  const [lessons, setLessons] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [earningsStatements, setEarningsStatements] = useState([]);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedReceiptImage, setSelectedReceiptImage] = useState('');
  const [selectedReceiptType, setSelectedReceiptType] = useState('');
  const [receiptModalMessage, setReceiptModalMessage] = useState('');
  const [isLoadingReceipt, setIsLoadingReceipt] = useState(false);
  const [showEarningsDetailsDialog, setShowEarningsDetailsDialog] = useState(false);
  const [selectedEarningsStatement, setSelectedEarningsStatement] = useState(null);
  const [earningsDetailsActiveTab, setEarningsDetailsActiveTab] = useState('workLessons');
  const [earningsWorkLessons, setEarningsWorkLessons] = useState([]);
  const [earningsExpenses, setEarningsExpenses] = useState([]);
  const [editingExpenseId, setEditingExpenseId] = useState(null);
  const [expenseMessage, setExpenseMessage] = useState('');
  const [expenseMessageType, setExpenseMessageType] = useState('');
  const [extractingExpenseIds, setExtractingExpenseIds] = useState([]);
  const [showDeleteExpenseDialog, setShowDeleteExpenseDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);
  const [editingLessonId, setEditingLessonId] = useState(null);
  const [lessonMessage, setLessonMessage] = useState('');
  const [lessonMessageType, setLessonMessageType] = useState('');
  const [activityLog, setActivityLog] = useState([]);
  const [activityUsernameFilter, setActivityUsernameFilter] = useState('');
  const [activityMethodFilter, setActivityMethodFilter] = useState('');
  const [quizzes, setQuizzes] = useState([]);
  const [showQuizDialog, setShowQuizDialog] = useState(false);
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [quizFormData, setQuizFormData] = useState({ category: '', topic: '', name: '', status: 'active' });
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [quizMessage, setQuizMessage] = useState('');
  const [quizMessageType, setQuizMessageType] = useState('');
  const [showQuizDeleteConfirm, setShowQuizDeleteConfirm] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [showOrganizationDialog, setShowOrganizationDialog] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const organizationDetailsRequest = React.useRef(0);
  const [organizationFormData, setOrganizationFormData] = useState({
    name: '', base: '', type: '', subscribedFeatures: '', address: '', city: '', state: '', zip: '', notes: '', status: 'active', aiDpeEnabled: false
  });
  const [organizationActiveTab, setOrganizationActiveTab] = useState('details');
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [organizationMessage, setOrganizationMessage] = useState('');
  const [organizationMessageType, setOrganizationMessageType] = useState('');
  const [showOrgUserDialog, setShowOrgUserDialog] = useState(false);
  const [selectedOrgUser, setSelectedOrgUser] = useState(null);
  const [reports, setReports] = useState({
    flightHours: [],
    aircraftUtilization: [],
    instructorActivity: [],
    revenueSummary: [],
    studentProgress: []
  });
  const [reportDateRange, setReportDateRange] = useState({
    startDate: (() => {
      const date = new Date();
      date.setDate(date.getDate() - 30);
      return date.toISOString().split('T')[0];
    })(),
    endDate: new Date().toISOString().split('T')[0]
  });
  const [activeReport, setActiveReport] = useState('flightHours');
  const [loadingReports, setLoadingReports] = useState(false);
  const [localSettings, setLocalSettings] = useState({
    logoImage: '',
    website: '',
    airports: 'KOWD,KBED',
    cautionWind: 15,
    maxWind: 25,
    cautionCeiling: 1000,
    minCeiling: 500,
    minVisibility: 3,
    cautionVisibility: 5,
    flightReservationDayStart: 6,
    flightReservationDayEnd: 22,
    simRate: 40,
    instructionOwnerAircraftFee: 10
  });
  const [newAirportCode, setNewAirportCode] = useState('');
  const [airportCodeError, setAirportCodeError] = useState('');
  const airportCodes = [...new Set(String(localSettings.airports || '').split(',').map(code => code.trim().toUpperCase()).filter(Boolean))];
  const addAirportCode = () => {
    const code = newAirportCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{3,4}$/.test(code)) {
      setAirportCodeError('Enter an airport code with 3 or 4 letters or numbers.');
      return;
    }
    if (airportCodes.includes(code)) {
      setAirportCodeError(`${code} is already in the list.`);
      return;
    }
    setLocalSettings(previous => ({ ...previous, airports: [...airportCodes, code].join(',') }));
    setNewAirportCode('');
    setAirportCodeError('');
  };
  const removeAirportCode = (codeToRemove) => {
    setLocalSettings(previous => ({
      ...previous,
      airports: String(previous.airports || '').split(',').map(code => code.trim().toUpperCase()).filter(code => code && code !== codeToRemove).join(',')
    }));
    setAirportCodeError('');
  };
  const [settingsMessage, setSettingsMessage] = useState('');
  const [settingsMessageType, setSettingsMessageType] = useState('');
  const [featureRequests, setFeatureRequests] = useState([]);
  const [showUserFeatureRequestsDialog, setShowUserFeatureRequestsDialog] = useState(false);
  const [userFeatureRequests, setUserFeatureRequests] = useState([]);
  const [editingFeatureRequestId, setEditingFeatureRequestId] = useState(null);
  const [featureRequestMessage, setFeatureRequestMessage] = useState('');
  const [featureRequestMessageType, setFeatureRequestMessageType] = useState('');
  const [editingAdminFeatureRequestId, setEditingAdminFeatureRequestId] = useState(null);
  const [showFeatureRequestDialog, setShowFeatureRequestDialog] = useState(false);
  const [selectedFeatureRequest, setSelectedFeatureRequest] = useState(null);
  const [featureRequestComments, setFeatureRequestComments] = useState([]);
  const [featureRequestCommentInput, setFeatureRequestCommentInput] = useState('');
  const [featureRequestCommentFile, setFeatureRequestCommentFile] = useState(null);
  const [selectedCommentAttachmentUrl, setSelectedCommentAttachmentUrl] = useState('');
  const featureRequestCommentFileInputRef = useRef(null);
  const [featureRequestCommentMessage, setFeatureRequestCommentMessage] = useState('');
  const [featureRequestCommentMessageType, setFeatureRequestCommentMessageType] = useState('');
  const [loadingFeatureRequestComments, setLoadingFeatureRequestComments] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [showAnnouncementDialog, setShowAnnouncementDialog] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [announcementAudience, setAnnouncementAudience] = useState('current');
  const [announcementFormData, setAnnouncementFormData] = useState({
    message: '',
    startDatetime: '',
    endDatetime: ''
  });
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementMessageType, setAnnouncementMessageType] = useState('');
  const [showAnnouncementDismissalsDialog, setShowAnnouncementDismissalsDialog] = useState(false);
  const [dismissedAnnouncementUsers, setDismissedAnnouncementUsers] = useState([]);
  const [dismissedAnnouncementLoading, setDismissedAnnouncementLoading] = useState(false);
  const [dismissedAnnouncementError, setDismissedAnnouncementError] = useState('');
  const [dismissedAnnouncement, setDismissedAnnouncement] = useState(null);
  const [maneuvers, setManeuvers] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [agreementMessage, setAgreementMessage] = useState('');
  const [agreementMessageType, setAgreementMessageType] = useState('');
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [newAgreementName, setNewAgreementName] = useState('');
  const [isAddingAgreement, setIsAddingAgreement] = useState(false);
  const [newAgreementRow, setNewAgreementRow] = useState({ name: '', file: null });
  const [viewingAgreementPdfUrl, setViewingAgreementPdfUrl] = useState('');
  const [showAgreementPdfModal, setShowAgreementPdfModal] = useState(false);
  const [showManeuverDialog, setShowManeuverDialog] = useState(false);
  const [selectedManeuver, setSelectedManeuver] = useState(null);
  const [maneuverFormData, setManeuverFormData] = useState({
    name: '',
    category: '',
    description: '',
    steps: ''
  });
  const [maneuverCategoryInput, setManeuverCategoryInput] = useState('');
  const [newManeuverCategoryName, setNewManeuverCategoryName] = useState('');
  const [maneuverMessage, setManeuverMessage] = useState('');
  const [maneuverMessageType, setManeuverMessageType] = useState('');
  const [editingManeuverId, setEditingManeuverId] = useState(null);
  const [selectedManeuverCategoryFilter, setSelectedManeuverCategoryFilter] = useState('');
  const [requirements, setRequirements] = useState([]);
  const [showRequirementDialog, setShowRequirementDialog] = useState(false);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [requirementFormData, setRequirementFormData] = useState({ requirement: '', stage: '', category: '' });
  const [requirementCategoryInput, setRequirementCategoryInput] = useState('');
  const [newRequirementCategoryName, setNewRequirementCategoryName] = useState('');
  const [requirementStageInput, setRequirementStageInput] = useState('');
  const [newRequirementStageName, setNewRequirementStageName] = useState('');
  const [requirementMessage, setRequirementMessage] = useState('');
  const [requirementMessageType, setRequirementMessageType] = useState('');
  const [editingRequirementId, setEditingRequirementId] = useState(null);
  const [selectedRequirementCategoryFilter, setSelectedRequirementCategoryFilter] = useState('');
  const [selectedRequirementStageFilter, setSelectedRequirementStageFilter] = useState('');
  const [showDeleteOrgDialog, setShowDeleteOrgDialog] = useState(false);
  const [deleteOrgMessage, setDeleteOrgMessage] = useState('');
  const [currentOrganization, setCurrentOrganization] = useState(null);
  const [loadingOrganization, setLoadingOrganization] = useState(false);
  const [deletingOrganization, setDeletingOrganization] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [selectedStudentId, setSelectedStudentId] = useState('');
  const [studentFilter, setStudentFilter] = useState('');
  const [selectedInstructorId, setSelectedInstructorId] = useState('');
  const [expenseFilter, setExpenseFilter] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const requirementCategoryOrder = ['private pilot', 'instrument rating', 'commercial pilot', 'multi-engine', 'multi-engine rating'];
  const sortRequirementCategories = (categories) => categories.slice().sort((a, b) => {
    const aIndex = requirementCategoryOrder.indexOf(a.toLowerCase());
    const bIndex = requirementCategoryOrder.indexOf(b.toLowerCase());
    if (aIndex !== -1 || bIndex !== -1) {
      return (aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex) - (bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex);
    }
    return a.localeCompare(b);
  });
  const maneuverCategories = [...new Set(maneuvers.map((m) => (m.category || '').trim()).filter((c) => c))].sort();
  const hasUncategorizedManeuvers = maneuvers.some((m) => !(m.category || '').trim());
  const filteredManeuvers = selectedManeuverCategoryFilter === ''
    ? maneuvers
    : selectedManeuverCategoryFilter === '__uncategorized__'
      ? maneuvers.filter((m) => !(m.category || '').trim())
      : maneuvers.filter((m) => ((m.category || '').trim() === selectedManeuverCategoryFilter));
  const requirementCategories = sortRequirementCategories([...new Set(requirements.map((r) => (r.category || '').trim()).filter((c) => c))]);
  const hasUncategorizedRequirements = requirements.some((r) => !(r.category || '').trim());
  const getRequirementsForCategory = (category) => {
    if (category === '') return requirements;
    if (category === '__uncategorized__') return requirements.filter((r) => !(r.category || '').trim());
    return requirements.filter((r) => ((r.category || '').trim() === category));
  };
  const getRequirementStagesForCategory = (category) => [
    ...new Set(getRequirementsForCategory(category).map((r) => (r.stage || '').trim()).filter((stage) => stage))
  ];
  const requirementStages = selectedRequirementCategoryFilter ? getRequirementStagesForCategory(selectedRequirementCategoryFilter) : [];
  const filteredRequirements = getRequirementsForCategory(selectedRequirementCategoryFilter).filter((requirement) => (
    !selectedRequirementCategoryFilter || !selectedRequirementStageFilter || (requirement.stage || '').trim() === selectedRequirementStageFilter
  ));
  const getInstructorActivityDualGiven = (item) => parseFloat(item?.total_dual_given) || 0;
  const getInstructorActivityGroundInstruction = (item) => parseFloat(item?.total_ground_instruction) || 0;
  const getInstructorActivityTotalHours = (item) => {
    if (item?.total_hours !== undefined && item?.total_hours !== null && item?.total_hours !== '') {
      return parseFloat(item.total_hours) || 0;
    }

    return getInstructorActivityDualGiven(item) + getInstructorActivityGroundInstruction(item);
  };
  const maxInstructorActivityHours = reports.instructorActivity.length > 0
    ? Math.max(...reports.instructorActivity.map((item) => getInstructorActivityTotalHours(item)), 0)
    : 0;

  useEffect(() => {
    if (selectedManeuverCategoryFilter === '') {
      return;
    }

    if (selectedManeuverCategoryFilter === '__uncategorized__' && !hasUncategorizedManeuvers) {
      setSelectedManeuverCategoryFilter('');
      return;
    }

    if (selectedManeuverCategoryFilter !== '__uncategorized__' && !maneuverCategories.includes(selectedManeuverCategoryFilter)) {
      setSelectedManeuverCategoryFilter('');
    }
  }, [selectedManeuverCategoryFilter, maneuverCategories, hasUncategorizedManeuvers]);

  useEffect(() => {
    if (selectedRequirementCategoryFilter === '') {
      if (selectedRequirementStageFilter !== '') {
        setSelectedRequirementStageFilter('');
      }
      return;
    }

    if (selectedRequirementCategoryFilter === '__uncategorized__' && !hasUncategorizedRequirements) {
      setSelectedRequirementCategoryFilter('');
      setSelectedRequirementStageFilter('');
      return;
    }

    if (selectedRequirementCategoryFilter !== '__uncategorized__' && !requirementCategories.includes(selectedRequirementCategoryFilter)) {
      setSelectedRequirementCategoryFilter('');
      setSelectedRequirementStageFilter('');
      return;
    }

    const stages = getRequirementStagesForCategory(selectedRequirementCategoryFilter);
    if (stages.length > 0 && !stages.includes(selectedRequirementStageFilter)) {
      setSelectedRequirementStageFilter(stages[0]);
      return;
    }

    if (stages.length === 0 && selectedRequirementStageFilter !== '') {
      setSelectedRequirementStageFilter('');
    }
  }, [selectedRequirementCategoryFilter, selectedRequirementStageFilter, requirementCategories, hasUncategorizedRequirements, requirements]);

  const selectRequirementCategoryFilter = (category) => {
    setSelectedRequirementCategoryFilter(category);
    const stages = category ? getRequirementStagesForCategory(category) : [];
    setSelectedRequirementStageFilter(stages[0] || '');
  };

  const fetchLessons = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/lesson/list/${startDate}/${endDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLessons(Array.isArray(data) ? data : []);
      } else {
        setLessons([]);
      }
    } catch (error) {
      setLessons([]);
    }
  };

  const fetchExpenses = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/expense/list/${startDate}/${endDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setExpenses(Array.isArray(data) ? data : []);
      } else {
        setExpenses([]);
      }
    } catch (error) {
      setExpenses([]);
    }
  };

  const fetchEarningsStatements = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/list/${startDate}/${endDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarningsStatements(Array.isArray(data) ? data : []);
      } else {
        setEarningsStatements([]);
      }
    } catch (error) {
      setEarningsStatements([]);
    }
  };

  const fetchActivityLog = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/activity-log/list/${startDate}/${endDate}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setActivityLog(Array.isArray(data) ? data : []);
      } else {
        setActivityLog([]);
      }
    } catch (error) {
      setActivityLog([]);
    }
  };

  const fetchQuizzes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setQuizzes(Array.isArray(data) ? data : []);
      } else {
        setQuizzes([]);
      }
    } catch (error) {
      setQuizzes([]);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/organization/list`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOrganizations(Array.isArray(data) ? data : []);
      } else {
        setOrganizations([]);
      }
    } catch (error) {
      setOrganizations([]);
    }
  };

  const saveOrganization = async () => {
    try {
      const payload = selectedOrganization?.id
        ? { ...organizationFormData, id: selectedOrganization.id }
        : organizationFormData;
      const response = await fetch(`${API_BASE_URL}/organization`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        window.dispatchEvent(new Event('organization-updated'));
        fetchOrganizations();
        setOrganizationMessage('Organization saved successfully');
        setOrganizationMessageType('success');
        setTimeout(() => {
          setOrganizationMessage('');
          setOrganizationMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setOrganizationMessage(`Error: ${errorText}`);
        setOrganizationMessageType('error');
        setTimeout(() => {
          setOrganizationMessage('');
          setOrganizationMessageType('');
        }, 5000);
      }
    } catch (error) {
      setOrganizationMessage(`Error: ${error.message}`);
      setOrganizationMessageType('error');
      setTimeout(() => {
        setOrganizationMessage('');
        setOrganizationMessageType('');
      }, 5000);
    }
  };

  const fetchOrganizationDetails = async (orgId, hydrateStatus = false) => {
    const request = ++organizationDetailsRequest.current;
    try {
      const response = await fetch(`${API_BASE_URL}/organization/${orgId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (request !== organizationDetailsRequest.current) return;
        setOrganizationUsers(data.users || []);
        if (hydrateStatus && Object.prototype.hasOwnProperty.call(data, 'status')) {
          setOrganizationFormData(previous => ({ ...previous, status: data.status ?? null }));
        }
      }
    } catch (error) {
      console.error('Error fetching organization details:', error);
    }
  };

  const removeUserFromOrganization = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({
          id: userId,
          organizationId: null
        })
      });
      
      if (response.ok) {
        fetchOrganizationDetails(selectedOrganization.id);
      }
    } catch (error) {
      console.error('Error removing user from organization:', error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const fetched = Array.isArray(data) ? (data[0] || null) : data;
        if (fetched && typeof fetched === 'object') {
          setSettings((prev) => ({
            ...prev,
            ...fetched,
            simRate: fetched.simRate != null ? fetched.simRate : prev.simRate,
            instructionOwnerAircraftFee: fetched.instructionOwnerAircraftFee != null ? fetched.instructionOwnerAircraftFee : prev.instructionOwnerAircraftFee
          }));
          setLocalSettings((prev) => ({
            ...prev,
            ...fetched,
            simRate: fetched.simRate != null ? fetched.simRate : prev.simRate,
            instructionOwnerAircraftFee: fetched.instructionOwnerAircraftFee != null ? fetched.instructionOwnerAircraftFee : prev.instructionOwnerAircraftFee
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const fetchCurrentOrganization = async () => {
    setLoadingOrganization(true);
    try {
      const response = await fetch(`${API_BASE_URL}/organization/user/current`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentOrganization(data);
      }
    } catch (error) {
      console.error('Error fetching current organization:', error);
    } finally {
      setLoadingOrganization(false);
    }
  };

  const fetchSingleReport = async (reportType) => {
    setLoadingReports(true);
    try {
      const endpoints = {
        flightHours: '/reports/flight-hours',
        aircraftUtilization: '/reports/aircraft-utilization', 
        instructorActivity: '/reports/instructor-activity',
        revenueSummary: '/reports/revenue-summary',
        studentProgress: '/reports/student-progress'
      };
      
      const endpoint = endpoints[reportType];
      if (!endpoint) return;
      
      const payload = reportType === 'studentProgress' ? {} : reportDateRange;
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setReports(prev => ({ ...prev, [reportType]: data }));
      }
    } catch (error) {
      console.error('Error fetching report:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchReports = async () => {
    setLoadingReports(true);
    try {
      const endpoints = {
        flightHours: '/reports/flight-hours',
        aircraftUtilization: '/reports/aircraft-utilization', 
        instructorActivity: '/reports/instructor-activity',
        revenueSummary: '/reports/revenue-summary',
        studentProgress: '/reports/student-progress'
      };
      
      const reportData = {};
      
      for (const [key, endpoint] of Object.entries(endpoints)) {
        const payload = key === 'studentProgress' ? {} : reportDateRange;
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'session-id': currentUser?.sessionId
          },
          body: JSON.stringify(payload)
        });
        
        if (response.ok) {
          reportData[key] = await response.json();
        } else {
          reportData[key] = [];
        }
      }
      
      setReports(reportData);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const fetchAgreements = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/list`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        setAgreements(await response.json());
      } else {
        setAgreements([]);
      }
    } catch (error) {
      setAgreements([]);
    }
  };

  const uploadAgreement = async () => {
    if (!newAgreementRow.name.trim()) {
      setAgreementMessage('Please enter a name for the agreement');
      setAgreementMessageType('error');
      setTimeout(() => { setAgreementMessage(''); setAgreementMessageType(''); }, 4000);
      return;
    }
    if (!newAgreementRow.file) {
      setAgreementMessage('Please select a PDF file');
      setAgreementMessageType('error');
      setTimeout(() => { setAgreementMessage(''); setAgreementMessageType(''); }, 4000);
      return;
    }
    setUploadingAgreement(true);
    try {
      const formData = new FormData();
      formData.append('agreementPdf', newAgreementRow.file);
      formData.append('name', newAgreementRow.name.trim());
      const response = await fetch(`${API_BASE_URL}/org-agreement`, {
        method: 'POST',
        headers: { 'session-id': currentUser?.sessionId },
        body: formData
      });
      if (response.ok) {
        setAgreementMessage('Agreement uploaded successfully');
        setAgreementMessageType('success');
        setIsAddingAgreement(false);
        setNewAgreementRow({ name: '', file: null });
        fetchAgreements();
      } else {
        const err = await response.text();
        setAgreementMessage(`Error: ${err}`);
        setAgreementMessageType('error');
      }
    } catch (error) {
      setAgreementMessage(`Error: ${error.message}`);
      setAgreementMessageType('error');
    } finally {
      setUploadingAgreement(false);
      setTimeout(() => { setAgreementMessage(''); setAgreementMessageType(''); }, 4000);
    }
  };

  const viewAgreement = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/${id}/pdf`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const blob = await response.blob();
        setViewingAgreementPdfUrl(URL.createObjectURL(blob));
        setShowAgreementPdfModal(true);
      }
    } catch (error) {
      console.error('Error loading agreement PDF:', error);
    }
  };

  const deleteAgreement = async (id) => {
    try {
      const response = await fetch(`${API_BASE_URL}/org-agreement/${id}`, {
        method: 'DELETE',
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        setAgreements(agreements.filter(a => a.id !== id));
        setAgreementMessage('Agreement deleted');
        setAgreementMessageType('success');
        setTimeout(() => { setAgreementMessage(''); setAgreementMessageType(''); }, 3000);
      }
    } catch (error) {
      setAgreementMessage(`Error: ${error.message}`);
      setAgreementMessageType('error');
      setTimeout(() => { setAgreementMessage(''); setAgreementMessageType(''); }, 5000);
    }
  };

  const fetchManeuvers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/maneuver/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setManeuvers(Array.isArray(data) ? data : []);
      } else {
        setManeuvers([]);
      }
    } catch (error) {
      setManeuvers([]);
    }
  };

  const saveManeuver = async () => {
    const effectiveCategory = maneuverCategoryInput === '__new__'
      ? (newManeuverCategoryName || '').trim()
      : (maneuverFormData.category || '').trim();

    if (!effectiveCategory) {
      setManeuverMessage('Error: Category is required');
      setManeuverMessageType('error');
      setTimeout(() => {
        setManeuverMessage('');
        setManeuverMessageType('');
      }, 3000);
      return;
    }

    try {
      const payloadData = {
        ...maneuverFormData,
        category: effectiveCategory
      };
      const payload = selectedManeuver?.id ? { id: selectedManeuver.id, ...payloadData } : payloadData;
      const response = await fetch(`${API_BASE_URL}/maneuver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setManeuverMessage('Maneuver saved successfully');
        setManeuverMessageType('success');
        fetchManeuvers();
        fetchSharedManeuvers();
        setShowManeuverDialog(false);
        setSelectedManeuver(null);
        setManeuverFormData({ name: '', category: '', description: '', steps: '' });
        setManeuverCategoryInput('');
        setNewManeuverCategoryName('');
        setTimeout(() => {
          setManeuverMessage('');
          setManeuverMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setManeuverMessage(`Error: ${errorText}`);
        setManeuverMessageType('error');
        setTimeout(() => {
          setManeuverMessage('');
          setManeuverMessageType('');
        }, 5000);
      }
    } catch (error) {
      setManeuverMessage(`Error: ${error.message}`);
      setManeuverMessageType('error');
      setTimeout(() => {
        setManeuverMessage('');
        setManeuverMessageType('');
      }, 5000);
    }
  };

  const updateManeuverField = (maneuverId, field, value) => {
    setManeuvers(prev => prev.map(maneuver => 
      maneuver.id === maneuverId ? { ...maneuver, [field]: value } : maneuver
    ));
  };

  const saveInlineManeuver = async (maneuver) => {
    try {
      const payload = {
        id: maneuver.id,
        name: maneuver.name,
        category: maneuver.category,
        description: maneuver.description,
        steps: maneuver.steps
      };
      
      const response = await fetch(`${API_BASE_URL}/maneuver`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setManeuverMessage('Maneuver updated successfully');
        setManeuverMessageType('success');
        fetchManeuvers();
        fetchSharedManeuvers();
        setEditingManeuverId(null);
        setTimeout(() => {
          setManeuverMessage('');
          setManeuverMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setManeuverMessage(`Error: ${errorText}`);
        setManeuverMessageType('error');
        setTimeout(() => {
          setManeuverMessage('');
          setManeuverMessageType('');
        }, 5000);
      }
    } catch (error) {
      setManeuverMessage(`Error: ${error.message}`);
      setManeuverMessageType('error');
      setTimeout(() => {
        setManeuverMessage('');
        setManeuverMessageType('');
      }, 5000);
    }
  };

  const fetchRequirements = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/requirement/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const records = Array.isArray(data) ? data : [];
        setRequirements(records);
        return records;
      } else {
        setRequirements([]);
        return [];
      }
    } catch (error) {
      setRequirements([]);
      return [];
    }
  };

  const saveRequirement = async () => {
    const effectiveCategory = requirementCategoryInput === '__new__'
      ? (newRequirementCategoryName || '').trim()
      : ((requirementFormData.category || '').trim() || (newRequirementCategoryName || '').trim());
    const effectiveStage = requirementStageInput === '__new__'
      ? (newRequirementStageName || '').trim()
      : ((requirementFormData.stage || '').trim() || (newRequirementStageName || '').trim());

    if (!(requirementFormData.requirement || '').trim() || !effectiveStage) {
      setRequirementMessage('Error: Requirement and stage are required');
      setRequirementMessageType('error');
      setTimeout(() => {
        setRequirementMessage('');
        setRequirementMessageType('');
      }, 3000);
      return;
    }

    try {
      const payloadData = {
        ...requirementFormData,
        requirement: requirementFormData.requirement.trim(),
        stage: effectiveStage,
        category: effectiveCategory || null
      };
      const payload = selectedRequirement?.id ? { id: selectedRequirement.id, ...payloadData } : payloadData;
      const response = await fetch(`${API_BASE_URL}/requirement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const savedRequirement = await response.json();
        setRequirementMessage('Requirement saved successfully');
        setRequirementMessageType('success');
        const refreshedRequirements = await fetchRequirements();
        setSelectedRequirementCategoryFilter(effectiveCategory || '');
        setSelectedRequirementStageFilter(effectiveStage);
        if (refreshedRequirements.length === 0 && savedRequirement?.id) {
          setRequirements(prev => [savedRequirement, ...prev.filter(item => item.id !== savedRequirement.id)]);
        }
        setShowRequirementDialog(false);
        setSelectedRequirement(null);
        setRequirementFormData({ requirement: '', stage: '', category: '' });
        setRequirementCategoryInput('');
        setNewRequirementCategoryName('');
        setRequirementStageInput('');
        setNewRequirementStageName('');
        setTimeout(() => {
          setRequirementMessage('');
          setRequirementMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setRequirementMessage(`Error: ${errorText}`);
        setRequirementMessageType('error');
        setTimeout(() => {
          setRequirementMessage('');
          setRequirementMessageType('');
        }, 5000);
      }
    } catch (error) {
      setRequirementMessage(`Error: ${error.message}`);
      setRequirementMessageType('error');
      setTimeout(() => {
        setRequirementMessage('');
        setRequirementMessageType('');
      }, 5000);
    }
  };

  const updateRequirementField = (requirementId, field, value) => {
    setRequirements(prev => prev.map(requirement =>
      requirement.id === requirementId ? { ...requirement, [field]: value } : requirement
    ));
  };

  const saveInlineRequirement = async (requirement) => {
    if (!(requirement.requirement || '').trim() || !(requirement.stage || '').trim()) {
      setRequirementMessage('Error: Requirement and stage are required');
      setRequirementMessageType('error');
      setTimeout(() => {
        setRequirementMessage('');
        setRequirementMessageType('');
      }, 3000);
      return;
    }

    try {
      const payload = {
        id: requirement.id,
        requirement: requirement.requirement.trim(),
        stage: requirement.stage.trim(),
        category: (requirement.category || '').trim() || null
      };

      const response = await fetch(`${API_BASE_URL}/requirement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        setRequirementMessage('Requirement updated successfully');
        setRequirementMessageType('success');
        fetchRequirements();
        setEditingRequirementId(null);
        setTimeout(() => {
          setRequirementMessage('');
          setRequirementMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setRequirementMessage(`Error: ${errorText}`);
        setRequirementMessageType('error');
        setTimeout(() => {
          setRequirementMessage('');
          setRequirementMessageType('');
        }, 5000);
      }
    } catch (error) {
      setRequirementMessage(`Error: ${error.message}`);
      setRequirementMessageType('error');
      setTimeout(() => {
        setRequirementMessage('');
        setRequirementMessageType('');
      }, 5000);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 30);
      
      const response = await fetch(`${API_BASE_URL}/announcement/list/${startDate.toISOString().split('T')[0]}/${endDate.toISOString().split('T')[0]}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(Array.isArray(data) ? data : []);
      } else {
        setAnnouncements([]);
      }
    } catch (error) {
      setAnnouncements([]);
    }
  };

  const showAnnouncementDismissals = async (announcement) => {
    setDismissedAnnouncement(announcement);
    setDismissedAnnouncementUsers([]);
    setDismissedAnnouncementError('');
    setDismissedAnnouncementLoading(true);
    setShowAnnouncementDismissalsDialog(true);

    try {
      const response = await fetch(`${API_BASE_URL}/user/list/announcement/${announcement.id}/dismissed`, {
        headers: { 'session-id': currentUser?.sessionId }
      });
      if (response.ok) {
        const data = await response.json();
        setDismissedAnnouncementUsers(Array.isArray(data) ? data : []);
      } else {
        const errorText = await response.text();
        setDismissedAnnouncementError(errorText || 'Unable to load dismissed users');
      }
    } catch (error) {
      setDismissedAnnouncementError('Unable to load dismissed users');
    } finally {
      setDismissedAnnouncementLoading(false);
    }
  };

  const formatDismissedAtLocal = (value) => {
    if (!value) return '';
    const source = String(value).trim();
    const match = source.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?/);
    const utcMilliseconds = match
      ? Date.UTC(
        Number(match[1]),
        Number(match[2]) - 1,
        Number(match[3]),
        Number(match[4]),
        Number(match[5]),
        Number(match[6] || 0)
      )
      : Date.parse(source);
    if (Number.isNaN(utcMilliseconds)) return source;

    const localDate = new Date(utcMilliseconds - (new Date().getTimezoneOffset() * 60 * 1000));
    const pad = (number) => String(number).padStart(2, '0');
    const hours = localDate.getUTCHours();
    const hour12 = hours % 12 || 12;
    const amPm = hours >= 12 ? 'PM' : 'AM';

    return `${pad(localDate.getUTCMonth() + 1)}/${pad(localDate.getUTCDate())}/${localDate.getUTCFullYear()} ${hour12}:${pad(localDate.getUTCMinutes())}:${pad(localDate.getUTCSeconds())} ${amPm}`;
  };

  const saveAnnouncement = async () => {
    try {
      const payload = {
        message: announcementFormData.message,
        startDatetime: announcementFormData.startDatetime,
        endDatetime: announcementFormData.endDatetime
      };
      
      if (selectedAnnouncement?.id) {
        payload.id = selectedAnnouncement.id;
      }
      if (currentUser?.user?.orgRole === 'superuser') {
        payload.orgId = selectedAnnouncement?.id
          ? selectedAnnouncement.orgId ?? null
          : announcementAudience === 'all' ? null : Number(currentUser?.user?.orgId);
        if (payload.orgId !== null && (!Number.isInteger(Number(payload.orgId)) || Number(payload.orgId) <= 0)) {
          throw new Error('Current organization is unavailable. Sign in to an organization before creating this announcement.');
        }
      }
      
      const response = await fetch(`${API_BASE_URL}/announcement`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setAnnouncementMessage('Announcement saved successfully');
        setAnnouncementMessageType('success');
        fetchAnnouncements();
        setShowAnnouncementDialog(false);
        setSelectedAnnouncement(null);
        setAnnouncementFormData({ message: '', startDatetime: '', endDatetime: '' });
        setTimeout(() => {
          setAnnouncementMessage('');
          setAnnouncementMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setAnnouncementMessage(`Error: ${errorText}`);
        setAnnouncementMessageType('error');
        setTimeout(() => {
          setAnnouncementMessage('');
          setAnnouncementMessageType('');
        }, 5000);
      }
    } catch (error) {
      setAnnouncementMessage(`Error: ${error.message}`);
      setAnnouncementMessageType('error');
      setTimeout(() => {
        setAnnouncementMessage('');
        setAnnouncementMessageType('');
      }, 5000);
    }
  };

  const sortFeatureRequests = (requests) => {
    const statusOrder = {
      in_progress: 0,
      open: 1,
      completed: 2,
      rejected: 3
    };
    const priorityOrder = {
      critical: 0,
      highest: 1,
      high: 2,
      medium: 3,
      low: 4
    };

    return [...requests].sort((a, b) => {
      const statusA = statusOrder[(a?.status || 'open').toLowerCase()] ?? 99;
      const statusB = statusOrder[(b?.status || 'open').toLowerCase()] ?? 99;
      if (statusA !== statusB) return statusA - statusB;

      const priorityA = priorityOrder[(a?.priority || 'medium').toLowerCase()] ?? 99;
      const priorityB = priorityOrder[(b?.priority || 'medium').toLowerCase()] ?? 99;
      if (priorityA !== priorityB) return priorityA - priorityB;

      return (a?.createdAt || '').localeCompare(b?.createdAt || '');
    });
  };

  const fetchFeatureRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/feature-request/all`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFeatureRequests(sortFeatureRequests(Array.isArray(data) ? data : []));
      } else {
        setFeatureRequests([]);
      }
    } catch (error) {
      setFeatureRequests([]);
    }
  };

  const fetchUserFeatureRequests = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/feature-request/list/user/${currentUser?.user?.id}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserFeatureRequests(Array.isArray(data) ? data : []);
      } else {
        setUserFeatureRequests([]);
      }
    } catch (error) {
      setUserFeatureRequests([]);
    }
  };

  const saveFeatureRequest = async (featureRequest) => {
    try {
      const payload = {
        title: featureRequest.title,
        description: featureRequest.description,
        priority: featureRequest.priority,
        status: featureRequest.status
      };
      
      if (featureRequest.id && !featureRequest.id.toString().startsWith('temp_')) {
        payload.id = featureRequest.id;
      }
      
      const response = await fetch(`${API_BASE_URL}/feature-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setFeatureRequestMessage('Feature request saved successfully');
        setFeatureRequestMessageType('success');
        fetchUserFeatureRequests();
        setEditingFeatureRequestId(null);
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setFeatureRequestMessage(`Error: ${errorText}`);
        setFeatureRequestMessageType('error');
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 5000);
      }
    } catch (error) {
      setFeatureRequestMessage(`Error: ${error.message}`);
      setFeatureRequestMessageType('error');
      setTimeout(() => {
        setFeatureRequestMessage('');
        setFeatureRequestMessageType('');
      }, 5000);
    }
  };

  const updateFeatureRequestField = (requestId, field, value) => {
    setUserFeatureRequests(prev => prev.map(request => 
      request.id === requestId ? { ...request, [field]: value } : request
    ));
  };

  const updateAdminFeatureRequestField = (requestId, field, value) => {
    setFeatureRequests(prev => sortFeatureRequests(prev.map(request => 
      request.id === requestId ? { ...request, [field]: value } : request
    )));
  };

  const saveAdminFeatureRequest = async (featureRequest) => {
    try {
      const payload = {
        id: featureRequest.id,
        title: featureRequest.title,
        description: featureRequest.description,
        priority: featureRequest.priority,
        status: featureRequest.status
      };
      
      const response = await fetch(`${API_BASE_URL}/feature-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setFeatureRequestMessage('Feature request updated successfully');
        setFeatureRequestMessageType('success');
        fetchFeatureRequests();
        setEditingAdminFeatureRequestId(null);
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setFeatureRequestMessage(`Error: ${errorText}`);
        setFeatureRequestMessageType('error');
        setTimeout(() => {
          setFeatureRequestMessage('');
          setFeatureRequestMessageType('');
        }, 5000);
      }
    } catch (error) {
      setFeatureRequestMessage(`Error: ${error.message}`);
      setFeatureRequestMessageType('error');
      setTimeout(() => {
        setFeatureRequestMessage('');
        setFeatureRequestMessageType('');
      }, 5000);
    }
  };

  const addNewFeatureRequest = () => {
    const tempId = `temp_${Date.now()}`;
    const newRequest = {
      id: tempId,
      title: '',
      description: '',
      priority: 'medium',
      status: 'open'
    };
    setUserFeatureRequests([newRequest, ...userFeatureRequests]);
    setEditingFeatureRequestId(tempId);
  };

  const loadFeatureRequestComments = async (featureRequestId) => {
    if (!featureRequestId) {
      setFeatureRequestComments([]);
      return;
    }

    setLoadingFeatureRequestComments(true);
    try {
      const response = await fetch(`${API_BASE_URL}/feature-request/comment/${featureRequestId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const data = await response.json();
        const comments = Array.isArray(data) ? data : data?.comments || data?.data?.comments || data?.data;
        setFeatureRequestComments(Array.isArray(comments) ? comments : []);
      } else {
        setFeatureRequestComments([]);
      }
    } catch (error) {
      setFeatureRequestComments([]);
    } finally {
      setLoadingFeatureRequestComments(false);
    }
  };

  const openFeatureRequestDialog = async (featureRequest) => {
    if (!featureRequest?.id || featureRequest.id.toString().startsWith('temp_')) {
      return;
    }

    setSelectedFeatureRequest({ ...featureRequest });
    setFeatureRequestCommentInput('');
    setFeatureRequestCommentFile(null);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
    setShowFeatureRequestDialog(true);
    await loadFeatureRequestComments(featureRequest.id);
  };

  const closeFeatureRequestDialog = () => {
    setShowFeatureRequestDialog(false);
    setSelectedFeatureRequest(null);
    setFeatureRequestComments([]);
    setFeatureRequestCommentInput('');
    setFeatureRequestCommentFile(null);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
  };

  const parseUtcCommentDatetime = (value) => {
    if (!value) return null;
    const rawValue = String(value).trim();
    const hasTimezone = /z$/i.test(rawValue) || /[+-]\d{2}:\d{2}$/.test(rawValue);
    const normalizedValue = hasTimezone ? rawValue : `${rawValue}Z`;
    const parsedDate = new Date(normalizedValue);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const formatCommentDatetimeLocal = (value) => {
    const parsedDate = parseUtcCommentDatetime(value);
    return parsedDate ? parsedDate.toLocaleString() : '';
  };

  const saveFeatureRequestComment = async () => {
    const featureRequestId = selectedFeatureRequest?.id;
    const comment = featureRequestCommentInput.trim();

    if (!featureRequestId || !comment) {
      setFeatureRequestCommentMessage('Comment cannot be empty');
      setFeatureRequestCommentMessageType('error');
      return;
    }

    try {
      const payload = {
        featureRequestId,
        commentDatetime: new Date().toISOString(),
        userId: currentUser?.user?.id || currentUser?.id,
        comment
      };
      let body = JSON.stringify(payload);
      const headers = {
        'Content-Type': 'application/json',
        'session-id': currentUser?.sessionId
      };

      if (featureRequestCommentFile) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
        formData.append('attachment', featureRequestCommentFile);
        body = formData;
        delete headers['Content-Type'];
      }

      const response = await fetch(`${API_BASE_URL}/feature-request/comment`, {
        method: 'POST',
        headers,
        body
      });

      if (response.ok) {
        setFeatureRequestCommentInput('');
        setFeatureRequestCommentFile(null);
        if (featureRequestCommentFileInputRef.current) featureRequestCommentFileInputRef.current.value = '';
        setFeatureRequestCommentMessage('Comment saved');
        setFeatureRequestCommentMessageType('success');
        await loadFeatureRequestComments(featureRequestId);
        setTimeout(() => {
          setFeatureRequestCommentMessage('');
          setFeatureRequestCommentMessageType('');
        }, 5000);
      } else {
        const errorText = await response.text();
        setFeatureRequestCommentMessage(`Error: ${errorText}`);
        setFeatureRequestCommentMessageType('error');
      }
    } catch (error) {
      setFeatureRequestCommentMessage(`Error: ${error.message}`);
      setFeatureRequestCommentMessageType('error');
    }
  };

  const handleFeatureRequestCommentFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) {
      setFeatureRequestCommentMessage('Attachment must be 1 MB or smaller');
      setFeatureRequestCommentMessageType('error');
      event.target.value = '';
      return;
    }
    setFeatureRequestCommentFile(file);
    setFeatureRequestCommentMessage('');
    setFeatureRequestCommentMessageType('');
  };

  const updateSelectedFeatureRequestField = (field, value) => {
    setSelectedFeatureRequest((prev) => prev ? { ...prev, [field]: value } : prev);
  };

  const saveSelectedFeatureRequest = async () => {
    if (!selectedFeatureRequest?.id) {
      return;
    }

    await saveAdminFeatureRequest(selectedFeatureRequest);
    const refreshed = featureRequests.find((request) => request.id === selectedFeatureRequest.id);
    setSelectedFeatureRequest(refreshed ? { ...refreshed } : { ...selectedFeatureRequest });
  };

  const saveSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(localSettings)
      });
      if (response.ok) {
        setSettings(prev => ({
          ...prev,
          ...localSettings,
          simRate: localSettings.simRate != null ? localSettings.simRate : prev.simRate,
          instructionOwnerAircraftFee: localSettings.instructionOwnerAircraftFee != null ? localSettings.instructionOwnerAircraftFee : prev.instructionOwnerAircraftFee
        }));
        setSettingsMessage('Settings saved successfully');
        setSettingsMessageType('success');
        setTimeout(() => {
          setSettingsMessage('');
          setSettingsMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setSettingsMessage(`Error: ${errorText}`);
        setSettingsMessageType('error');
        setTimeout(() => {
          setSettingsMessage('');
          setSettingsMessageType('');
        }, 5000);
      }
    } catch (error) {
      setSettingsMessage(`Error: ${error.message}`);
      setSettingsMessageType('error');
      setTimeout(() => {
        setSettingsMessage('');
        setSettingsMessageType('');
      }, 5000);
    }
  };

  const handleLogoImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSettingsMessage('Please select an image file');
      setSettingsMessageType('error');
      event.target.value = '';
      return;
    }
    if (file.size > 100 * 1024) {
      setSettingsMessage('Logo image must be 100 KB or smaller');
      setSettingsMessageType('error');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const logoImage = typeof reader.result === 'string' ? reader.result : '';
      setLocalSettings((prev) => ({ ...prev, logoImage }));
      setSettingsMessage('');
      setSettingsMessageType('');
    };
    reader.onerror = () => {
      setSettingsMessage('Unable to read the selected image');
      setSettingsMessageType('error');
    };
    reader.readAsDataURL(file);
  };

  const loadQuizWithQuestions = async (quizId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/${quizId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedQuiz(data);
        setQuizFormData({ category: data.category, topic: data.topic, name: data.name, status: data.status || 'active' });
        setQuizQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error loading quiz:', error);
    }
  };

  const saveQuiz = async () => {
    try {
      const payload = selectedQuiz?.id ? { id: selectedQuiz.id, ...quizFormData } : quizFormData;
      payload.status = payload.status.toLowerCase();
      const response = await fetch(`${API_BASE_URL}/quiz`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        const data = await response.json();
        setSelectedQuiz(data);
        fetchQuizzes();
        setQuizMessage('Quiz saved successfully');
        setQuizMessageType('success');
        setTimeout(() => {
          setQuizMessage('');
          setQuizMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setQuizMessage(`Error: ${errorText}`);
        setQuizMessageType('error');
        setTimeout(() => {
          setQuizMessage('');
          setQuizMessageType('');
        }, 5000);
      }
    } catch (error) {
      setQuizMessage(`Error: ${error.message}`);
      setQuizMessageType('error');
      setTimeout(() => {
        setQuizMessage('');
        setQuizMessageType('');
      }, 5000);
    }
  };

  const saveQuestion = async (question) => {
    try {
      const formData = new FormData();
      
      if (question.id && !question.id.toString().startsWith('temp_')) {
        formData.append('id', question.id);
      }
      formData.append('quizId', selectedQuiz.id);
      formData.append('question', question.question || '');
      formData.append('choiceA', question.choiceA || '');
      formData.append('choiceB', question.choiceB || '');
      formData.append('choiceC', question.choiceC || '');
      formData.append('choiceD', question.choiceD || '');
      formData.append('correctAnswer', question.correctAnswer || 'A');
      
      if (question.image1File) {
        formData.append('image1', question.image1File);
      }
      if (question.image2File) {
        formData.append('image2', question.image2File);
      }
      
      const response = await fetch(`${API_BASE_URL}/quiz/question`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: formData
      });
      
      if (response.ok) {
        loadQuizWithQuestions(selectedQuiz.id);
        setEditingQuestionId(null);
      } else {
        const errorText = await response.text();
        console.error('Error saving question:', response.status, errorText);
      }
    } catch (error) {
      console.error('Error saving question:', error);
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/question/${questionId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        loadQuizWithQuestions(selectedQuiz.id);
      }
    } catch (error) {
      console.error('Error deleting question:', error);
    }
  };

  const deleteQuiz = async (quizId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/quiz/${quizId}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        fetchQuizzes();
      }
    } catch (error) {
      console.error('Error deleting quiz:', error);
    }
  };

  const newQuestionRef = useRef(null);

  const addNewQuestion = () => {
    const tempId = `temp_${Date.now()}`;
    setQuizQuestions([...quizQuestions, { id: tempId, question: '', choiceA: '', choiceB: '', choiceC: '', choiceD: '', correctAnswer: 'A', image1File: null, image2File: null }]);
    setEditingQuestionId(tempId);
    setTimeout(() => {
      newQuestionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 100);
  };

  const updateQuestionField = (questionId, field, value) => {
    setQuizQuestions(prev => prev.map(q => q.id === questionId ? { ...q, [field]: value } : q));
  };

  const loadEarningsStatementItems = async (earningsStatementId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/earnings-statement/items/${earningsStatementId}`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEarningsWorkLessons(data.workLessons || []);
        setEarningsExpenses(data.expenses || []);
      }
    } catch (error) {
      console.error('Error loading earnings statement items:', error);
    }
  };

  const handleEarningsStatementClick = (earningsStatement) => {
    setSelectedEarningsStatement(earningsStatement);
    setEarningsDetailsActiveTab('workLessons');
    loadEarningsStatementItems(earningsStatement.id);
    setShowEarningsDetailsDialog(true);
  };

  useEffect(() => {
    if (currentUser?.sessionId) {
      if (activeTab === 'lessons' && startDate && endDate) {
        fetchLessons();
      } else if (activeTab === 'expenses' && startDate && endDate) {
        fetchExpenses();
      } else if (activeTab === 'earnings' && startDate && endDate) {
        fetchEarningsStatements();
      } else if (activeTab === 'activity' && startDate && endDate) {
        fetchActivityLog();
      } else if (activeTab === 'quizzes') {
        fetchQuizzes();
      } else if (activeTab === 'organizations') {
        fetchOrganizations();
      } else if (activeTab === 'requirements') {
        fetchRequirements();
      } else if (activeTab === 'settings') {
        setLocalSettings(settings);
        fetchCurrentOrganization();
      } else if (activeTab === 'reports') {
        fetchReports();
      } else if (activeTab === 'featureRequests') {
        fetchFeatureRequests();
      } else if (activeTab === 'announcements') {
        fetchAnnouncements();
      } else if (activeTab === 'maneuvers') {
        fetchManeuvers();
      } else if (activeTab === 'agreements') {
        fetchAgreements();
      }
    }
  }, [currentUser?.sessionId, startDate, endDate, activeTab]);

  const getStudentName = (userId) => {
    const student = (Array.isArray(people) ? people : []).find(p => p.id === userId);
    return student ? `${student.firstName || student.first_name || ''} ${student.lastName || student.last_name || ''}`.trim() : 'Unknown';
  };

  const getInstructorName = (instructorId) => {
    const instructor = (Array.isArray(people) ? people : []).find(p => p.id === instructorId);
    return instructor ? `${instructor.firstName || instructor.first_name || ''} ${instructor.lastName || instructor.last_name || ''}`.trim() : 'Unknown';
  };

  const getAircraftInfo = (aircraftId) => {
    const aircraftItem = (Array.isArray(aircraft) ? aircraft : []).find(a => a.id === aircraftId);
    return aircraftItem ? `${aircraftItem.model || ''} - ${aircraftItem.tailNumber || aircraftItem.registration || ''}`.trim() : 'Unknown';
  };

  const getUserName = (userId) => {
    const user = (Array.isArray(people) ? people : []).find(p => p.id === userId);
    return user ? `${user.firstName || user.first_name || ''} ${user.lastName || user.last_name || ''}`.trim() : 'Unknown';
  };

  const cleanDescription = (description) => {
    if (!description) return '';
    try {
      // Parse JSON if it's a JSON string
      const parsed = JSON.parse(description);
      // Remove id field and return as clean string
      const { id, ...cleanObj } = parsed;
      return JSON.stringify(cleanObj).replace(/[{}"]/g, '').replace(/,/g, ', ');
    } catch {
      // If not JSON, just remove curly braces and clean up
      return description.replace(/[{}"]/g, '').replace(/,/g, ', ');
    }
  };

  const updateLessonField = (lessonId, field, value) => {
    setLessons(prev => prev.map(lesson => 
      lesson.id === lessonId ? { ...lesson, [field]: value } : lesson
    ));
  };

  const saveLesson = async (lesson) => {
    try {
      const payload = {
        id: lesson.id,
        userId: lesson.userId,
        reservationId: lesson.reservationId,
        date: lesson.date,
        aircraftId: lesson.aircraftId,
        instructorId: lesson.instructorId,
        startTime: lesson.startTime,
        endTime: lesson.endTime,
        flightTime: parseFloat(lesson.flightTime) || null,
        simulator: parseFloat(lesson.simulator) || null,
        dualReceived: parseFloat(lesson.dualReceived) || null,
        ground: parseFloat(lesson.ground) || null,
        debrief: lesson.debrief,
        nextFlight: lesson.nextFlight,
        purchases: parseFloat(lesson.purchases) || null,
        credit: parseFloat(lesson.credit) || null,
        fee: parseFloat(lesson.fee) || 0,
        payment: parseFloat(lesson.payment) || null,
        paymentType: lesson.paymentType
      };
      
      const response = await fetch(`${API_BASE_URL}/lesson`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        setLessonMessage('Lesson saved successfully');
        setLessonMessageType('success');
        setEditingLessonId(null);
        setTimeout(() => {
          setLessonMessage('');
          setLessonMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setLessonMessage(`Error: ${errorText}`);
        setLessonMessageType('error');
        setTimeout(() => {
          setLessonMessage('');
          setLessonMessageType('');
        }, 5000);
      }
    } catch (error) {
      setLessonMessage(`Error: ${error.message}`);
      setLessonMessageType('error');
      setTimeout(() => {
        setLessonMessage('');
        setLessonMessageType('');
      }, 5000);
    }
  };

  const updateExpenseField = async (expenseId, field, value) => {
    if (field === 'receiptFile' && value && value.size > 1024 * 1024) {
      setExpenseMessage('Error: File size must be under 1MB');
      setExpenseMessageType('error');
      setTimeout(() => {
        setExpenseMessage('');
        setExpenseMessageType('');
      }, 5000);
      return;
    }

    setExpenses((prev) => prev.map((expense) => (
      expense.id === expenseId ? { ...expense, [field]: value } : expense
    )));

    if (field === 'receiptFile' && value) {
      setExtractingExpenseIds((prev) => [...new Set([...prev, expenseId])]);
      try {
        const extractedFields = await extractExpenseReceipt(value, currentUser?.sessionId, aircraft, settings?.airports);
        if (extractedFields) {
          setExpenses((prev) => prev.map((expense) => (
            expense.id === expenseId && expense.receiptFile === value
              ? { ...expense, ...extractedFields }
              : expense
          )));
        }
      } catch (error) {
        setExpenseMessage(`Receipt extraction failed: ${error.message}`);
        setExpenseMessageType('error');
        setTimeout(() => {
          setExpenseMessage('');
          setExpenseMessageType('');
        }, 5000);
      } finally {
        setExtractingExpenseIds((prev) => prev.filter((id) => id !== expenseId));
      }
    }
  };

  const handleExpenseReceiptChange = async (expenseId, file) => {
    if (!file) return;
    if (file.size > 1048576) {
      updateExpenseField(expenseId, 'receiptFile', file);
      return;
    }
    updateExpenseField(expenseId, 'receiptFile', file);
    if (!file.type.startsWith('image/')) return;

    setExpenseMessage('Scanning receipt...');
    setExpenseMessageType('info');
    try {
      const result = await extractReceipt(file, currentUser?.sessionId);
      const extractedFields = mapReceiptToExpense(result, aircraft);
      setExpenses(prev => prev.map(expense => (
        expense.id === expenseId && expense.receiptFile === file
          ? { ...expense, ...extractedFields }
          : expense
      )));
      setExpenseMessage('Receipt scanned. Review the extracted fields before saving.');
      setExpenseMessageType('success');
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
      setExpenseMessageType('error');
    }
  };

  const saveExpense = async (expense) => {
    try {
      const formData = new FormData();

      if (expense.id && !expense.id.toString().startsWith('temp_')) {
        formData.append('id', expense.id);
      }
      formData.append('date', expense.date || '');
      formData.append('expense', expense.expense || '');
      formData.append('vendor', expense.vendor || '');
      formData.append('total', parseFloat(expense.total) || 0);

      if (expense.userId) {
        formData.append('userId', expense.userId);
      }
      if (expense.earningStatementId) {
        formData.append('earningStatementId', expense.earningStatementId);
      }
      if (expense.aircraftId) {
        formData.append('aircraftId', expense.aircraftId);
      }
      if (expense.details) {
        formData.append('details', expense.details);
      }
      if (expense.receiptFile) {
        formData.append('receiptImage', expense.receiptFile);
      }

      const response = await fetch(`${API_BASE_URL}/expense`, {
        method: 'POST',
        headers: {
          'session-id': currentUser?.sessionId
        },
        body: formData
      });

      if (response.ok) {
        setExpenseMessage('Expense saved successfully');
        setExpenseMessageType('success');
        setEditingExpenseId(null);
        fetchExpenses();
        setTimeout(() => {
          setExpenseMessage('');
          setExpenseMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setExpenseMessage(`Error: ${errorText}`);
        setExpenseMessageType('error');
        setTimeout(() => {
          setExpenseMessage('');
          setExpenseMessageType('');
        }, 5000);
      }
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
      setExpenseMessageType('error');
      setTimeout(() => {
        setExpenseMessage('');
        setExpenseMessageType('');
      }, 5000);
    }
  };

  const openExpenseReceipt = async (expense) => {
    setShowReceiptModal(true);
    setIsLoadingReceipt(true);
    setReceiptModalMessage('');
    setSelectedReceiptImage('');
    setSelectedReceiptType('');

    const receiptImageValue = typeof expense?.receiptImage === 'string' ? expense.receiptImage.trim() : '';
    if (receiptImageValue) {
      const resolvedReceiptImage = /^https?:\/\//i.test(receiptImageValue) || receiptImageValue.startsWith('data:') || receiptImageValue.startsWith('blob:')
        ? receiptImageValue
        : `${API_BASE_URL}/${receiptImageValue.replace(/^\//, '')}`;
      setSelectedReceiptImage(resolvedReceiptImage);
      setSelectedReceiptType(/\.pdf(?:$|\?)/i.test(receiptImageValue) ? 'pdf' : 'image');
      setIsLoadingReceipt(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/expense/${expense.id}/image`, {
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const imageUrl = URL.createObjectURL(blob);
        setSelectedReceiptImage(imageUrl);
        setSelectedReceiptType(blob.type === 'application/pdf' ? 'pdf' : 'image');
      } else {
        const errorText = await response.text();
        setReceiptModalMessage(errorText || 'Unable to load receipt image');
      }
    } catch (error) {
      setReceiptModalMessage('Unable to load receipt image');
    }

    setIsLoadingReceipt(false);
  };

  const closeReceiptModal = () => {
    if (selectedReceiptImage.startsWith('blob:')) {
      URL.revokeObjectURL(selectedReceiptImage);
    }
    setShowReceiptModal(false);
    setSelectedReceiptImage('');
    setSelectedReceiptType('');
    setReceiptModalMessage('');
    setIsLoadingReceipt(false);
  };

  const requestDeleteExpense = (expense) => {
    setExpenseToDelete(expense);
    setShowDeleteExpenseDialog(true);
  };

  const confirmDeleteExpense = async () => {
    if (!expenseToDelete?.id) {
      setShowDeleteExpenseDialog(false);
      setExpenseToDelete(null);
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/expense/${expenseToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'session-id': currentUser?.sessionId
        }
      });

      if (response.ok) {
        setExpenses((prev) => prev.filter((item) => item.id !== expenseToDelete.id));
        if (editingExpenseId === expenseToDelete.id) {
          setEditingExpenseId(null);
        }
        setExpenseMessage('Expense deleted successfully');
        setExpenseMessageType('success');
        setTimeout(() => {
          setExpenseMessage('');
          setExpenseMessageType('');
        }, 3000);
      } else {
        const errorText = await response.text();
        setExpenseMessage(`Error: ${errorText}`);
        setExpenseMessageType('error');
        setTimeout(() => {
          setExpenseMessage('');
          setExpenseMessageType('');
        }, 5000);
      }
    } catch (error) {
      setExpenseMessage(`Error: ${error.message}`);
      setExpenseMessageType('error');
      setTimeout(() => {
        setExpenseMessage('');
        setExpenseMessageType('');
      }, 5000);
    } finally {
      setShowDeleteExpenseDialog(false);
      setExpenseToDelete(null);
    }
  };

  const isAdmin = ['admin', 'superuser'].includes(currentUser?.user?.orgRole);
  const isSuperuser = currentUser?.user?.orgRole === 'superuser';
  const manageTabGroups = [
    {
      key: 'operations',
      label: 'Operations',
      tabs: [
        { key: 'lessons', label: 'Lessons' },
        { key: 'activity', label: 'Activity Log' },
        { key: 'reports', label: 'Reports' }
      ]
    },
    {
      key: 'finance',
      label: 'Finance',
      tabs: [
        { key: 'expenses', label: 'Expenses' },
        { key: 'earnings', label: 'Earnings Statements' }
      ]
    },
    {
      key: 'training',
      label: 'Training',
      tabs: [
        { key: 'quizzes', label: 'Quizzes' },
        ...(showExaminer && (isAdmin || currentUser?.user?.orgRole === 'instructor') ? [{ key: 'examiner', label: 'AI Mock Examiner' }] : []),
        ...(isAdmin ? [{ key: 'requirements', label: 'Requirements' }, { key: 'maneuvers', label: 'Maneuvers' }, { key: 'trainingPackages', label: 'Training Packages' }] : [])
      ]
    },
    {
      key: 'organization',
      label: 'Organization',
      tabs: [
        ...(isAdmin ? [{ key: 'announcements', label: 'Announcements' }, { key: 'agreements', label: 'Agreements' }, { key: 'locations', label: 'Locations' }, { key: 'website', label: 'Website' }, { key: 'reservationTypes', label: 'Reservation Types' }, { key: 'forms', label: 'Forms' }] : [])
      ]
    },
    {
      key: 'admin',
      label: 'Admin',
      tabs: [{ key: 'settings', label: 'Settings' }, ...(isAdmin ? [{ key: 'chatbot', label: 'Website Chatbot' }] : [])]
    },
    ...(isSuperuser ? [{
      key: 'appAdmin',
      label: 'App Admin',
      tabs: [
        { key: 'organizations', label: 'Organizations' },
        { key: 'featureRequests', label: 'Feature Requests' }
      ]
    }] : [])
  ].filter(group => group.tabs.length > 0);
  const activeManageGroup = manageTabGroups.find(group => group.tabs.some(tab => tab.key === activeTab)) || manageTabGroups[0];
  const manageGroupColors = {
    operations: { active: '#0f766e', inactive: '#f0fdfa' },
    finance: { active: '#16a34a', inactive: '#f0fdf4' },
    training: { active: '#7c3aed', inactive: '#f5f3ff' },
    organization: { active: '#0891b2', inactive: '#ecfeff' },
    admin: { active: '#475569', inactive: '#f8fafc' },
    appAdmin: { active: '#9333ea', inactive: '#faf5ff' }
  };
  const manageTabColors = {
    lessons: { active: '#2563eb', inactive: '#eff6ff', text: '#1e3a8a' },
    expenses: { active: '#16a34a', inactive: '#f0fdf4', text: '#166534' },
    earnings: { active: '#d97706', inactive: '#fffbeb', text: '#92400e' },
    activity: { active: '#0891b2', inactive: '#ecfeff', text: '#155e75' },
    quizzes: { active: '#7c3aed', inactive: '#f5f3ff', text: '#5b21b6' },
    reports: { active: '#0f766e', inactive: '#f0fdfa', text: '#115e59' },
    requirements: { active: '#be123c', inactive: '#fff1f2', text: '#9f1239' },
    announcements: { active: '#ca8a04', inactive: '#fefce8', text: '#854d0e' },
    maneuvers: { active: '#4f46e5', inactive: '#eef2ff', text: '#3730a3' },
    agreements: { active: '#475569', inactive: '#f8fafc', text: '#334155' },
    settings: { active: '#0d9488', inactive: '#f0fdfa', text: '#0f766e' },
    organizations: { active: '#9333ea', inactive: '#faf5ff', text: '#6b21a8' },
    featureRequests: { active: '#db2777', inactive: '#fdf2f8', text: '#9d174d' }
  };

  const getManageTabStyle = (tab, overrides = {}) => {
    const colors = manageTabColors[tab] || manageTabColors.settings;
    const isActive = activeTab === tab;
    return {
      padding: '1rem 0.90396rem',
      backgroundColor: isActive ? colors.active : colors.inactive,
      color: isActive ? 'white' : colors.text,
      border: 'none',
      borderRight: '1px solid #dee2e6',
      cursor: 'pointer',
      fontWeight: isActive ? 'bold' : 'normal',
      ...overrides
    };
  };

  return (
    <>
      <div className="manage-page" style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ marginBottom: '2rem', color: '#343a40' }}>Manage</h1>
        
        <div style={{ border: '1px solid #dee2e6', borderRadius: '6px 6px 0 0', overflow: 'hidden' }}>
          <div aria-label="Manage categories" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '0.15rem', padding: '0.5rem 0.75rem 0', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
            {manageTabGroups.map(group => {
              const isActive = group.key === activeManageGroup.key;
              const colors = manageGroupColors[group.key];
              return (
                <button
                  key={group.key}
                  aria-pressed={isActive}
                  onClick={() => { if (isFormsRoute) navigate('/manage'); setActiveTab(group.tabs[0].key); }}
                  style={{ padding: '0.65rem 1rem', borderTop: `1px solid ${isActive ? colors.active : 'transparent'}`, borderLeft: `1px solid ${isActive ? colors.active : 'transparent'}`, borderRight: `1px solid ${isActive ? colors.active : 'transparent'}`, borderBottom: `1px solid ${isActive ? 'white' : 'transparent'}`, borderRadius: '5px 5px 0 0', marginBottom: '-1px', backgroundColor: isActive ? colors.active : colors.inactive, color: isActive ? 'white' : '#475569', cursor: 'pointer', fontWeight: isActive ? 600 : 500 }}
                >
                  {group.label}
                </button>
              );
            })}
          </div>
          <div className="scheduling-manage-tabs" aria-label={`${activeManageGroup.label} sections`} tabIndex={0} style={{ display: 'flex', alignItems: 'flex-end', gap: '0.15rem', overflowX: 'auto', padding: '0.5rem 0.75rem 0', backgroundColor: 'white', borderBottom: '1px solid #dee2e6' }}>
            {activeManageGroup.tabs.map(tab => {
              const isActive = activeTab === tab.key;
              const colors = manageGroupColors[activeManageGroup.key];
              return (
                <button
                  key={tab.key}
                  aria-pressed={isActive}
                  onClick={() => { if (tab.key === 'forms') navigate('/manage/forms'); else if (isFormsRoute) navigate('/manage'); setActiveTab(tab.key); }}
                  style={{ flexShrink: 0, whiteSpace: 'nowrap', padding: '0.6rem 0.9rem', borderTop: `1px solid ${isActive ? colors.active : colors.inactive}`, borderLeft: `1px solid ${isActive ? colors.active : colors.inactive}`, borderRight: `1px solid ${isActive ? colors.active : colors.inactive}`, borderBottom: `1px solid ${isActive ? 'white' : colors.inactive}`, borderRadius: '5px 5px 0 0', marginBottom: '-1px', backgroundColor: isActive ? colors.active : colors.inactive, color: isActive ? 'white' : colors.active, cursor: 'pointer', fontWeight: isActive ? 600 : 500 }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="manage-page-content" style={{ backgroundColor: 'white', border: '1px solid #dee2e6', borderRadius: '0 0 8px 8px', padding: '2rem' }}>
          {activeTab === 'locations' && isAdmin && <OrganizationLocations />}
          {activeTab === 'website' && isAdmin && <OrganizationWebsite onOpenLocationSettings={() => setActiveTab('locations')} />}
          {activeTab === 'reservationTypes' && isAdmin && <OrganizationCatalog kind="reservationTypes" />}
          {activeTab === 'trainingPackages' && isAdmin && <OrganizationCatalog kind="trainingPackages" />}
          {activeTab === 'chatbot' && isAdmin && <WebsiteChatbotSettings />}
          {showExaminer && activeTab === 'examiner' && (isAdmin || currentUser?.user?.orgRole === 'instructor') && <ExaminerManagement />}
          {activeTab === 'forms' && isAdmin && <Forms />}
          {activeTab === 'lessons' && (
            <div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Student:</label>
                  <input 
                    type="text" 
                    value={studentFilter} 
                    onChange={(e) => setStudentFilter(e.target.value)}
                    placeholder="Type student name..."
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Instructor:</label>
                  <select 
                    value={selectedInstructorId} 
                    onChange={(e) => setSelectedInstructorId(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }}
                  >
                    <option value="">All Instructors</option>
                    {people.filter(p => p.role === 'instructor' || p.roleId === 3).map(instructor => (
                      <option key={instructor.id} value={instructor.id}>
                        {instructor.firstName} {instructor.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button 
                    onClick={fetchLessons}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {lessonMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: lessonMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: lessonMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${lessonMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {lessonMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto', maxHeight: '500px', border: '1px solid #dee2e6' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Student</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Instructor</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Aircraft</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', minWidth: '108px' }}>Time</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Flight</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Dual</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Ground</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Purchases</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Fee</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Payment</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Credit</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Payment Type</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredLessons = lessons.filter(lesson => {
                        const studentName = getStudentName(lesson.userId).toLowerCase();
                        const studentMatch = !studentFilter || studentName.includes(studentFilter.toLowerCase());
                        const instructorMatch = !selectedInstructorId || lesson.instructorId == selectedInstructorId;
                        return studentMatch && instructorMatch;
                      });
                      
                      if (filteredLessons.length === 0) {
                        return (
                          <tr>
                            <td colSpan="14" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                              No lessons found for the selected criteria
                            </td>
                          </tr>
                        );
                      }
                      
                      return [
                        ...filteredLessons.map((lesson) => {
                          const isEditing = editingLessonId === lesson.id;
                          return (
                            <tr key={lesson.id}>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                <div style={{ display: 'flex', gap: '0.25rem' }}>
                                  {isEditing ? (
                                    <>
                                      <button onClick={() => saveLesson(lesson)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                      <button onClick={() => setEditingLessonId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                    </>
                                  ) : (
                                    <button onClick={() => setEditingLessonId(lesson.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                  )}
                                </div>
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {getStudentName(lesson.userId)}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {getInstructorName(lesson.instructorId)}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {lesson.aircraftId ? getAircraftInfo(lesson.aircraftId) : 'Ground Only'}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {lesson.startTime && lesson.endTime ? `${lesson.startTime} - ${lesson.endTime}` : ''}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.1" value={lesson.flightTime || ''} onChange={(e) => updateLessonField(lesson.id, 'flightTime', e.target.value)} style={{ width: '80px', border: 'none' }} /> : (lesson.flightTime || 0)}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.1" value={lesson.dualReceived || ''} onChange={(e) => updateLessonField(lesson.id, 'dualReceived', e.target.value)} style={{ width: '80px', border: 'none' }} /> : (lesson.dualReceived || 0)}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.1" value={lesson.ground || ''} onChange={(e) => updateLessonField(lesson.id, 'ground', e.target.value)} style={{ width: '80px', border: 'none' }} /> : (lesson.ground || 0)}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.01" value={lesson.purchases || ''} onChange={(e) => updateLessonField(lesson.id, 'purchases', e.target.value)} style={{ width: '80px', border: 'none' }} /> : `$${lesson.purchases || 0}`}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.01" value={lesson.fee || ''} onChange={(e) => updateLessonField(lesson.id, 'fee', e.target.value)} style={{ width: '80px', border: 'none' }} /> : `$${lesson.fee || 0}`}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.01" value={lesson.payment || ''} onChange={(e) => updateLessonField(lesson.id, 'payment', e.target.value)} style={{ width: '80px', border: 'none' }} /> : `$${lesson.payment || 0}`}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="number" step="0.01" value={lesson.credit || ''} onChange={(e) => updateLessonField(lesson.id, 'credit', e.target.value)} style={{ width: '80px', border: 'none' }} /> : `$${lesson.credit || 0}`}
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                {isEditing ? <input type="text" value={lesson.paymentType || ''} onChange={(e) => updateLessonField(lesson.id, 'paymentType', e.target.value)} style={{ width: '100px', border: 'none' }} /> : (lesson.paymentType || '')}
                              </td>
                            </tr>
                          );
                        }),
                        filteredLessons.length > 0 && (
                          <tr key="totals" style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                            <td colSpan="6" style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'right' }}>Totals:</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.flightTime) || 0), 0).toFixed(1)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.dualReceived) || 0), 0).toFixed(1)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.ground) || 0), 0).toFixed(1)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              ${filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.purchases) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              ${filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.fee) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              ${filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.credit) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              ${filteredLessons.reduce((sum, lesson) => sum + (parseFloat(lesson.payment) || 0), 0).toFixed(2)}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}></td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}></td>
                          </tr>
                        )
                      ];
                    })()}
                  </tbody>
                </table>
              </div>
              
              {lessons.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
                  Total lessons: {lessons.length}
                </div>
              )}
            </div>
          )}

          {activeTab === 'expenses' && (
            <div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Filter Expense:</label>
                  <input 
                    type="text" 
                    value={expenseFilter} 
                    onChange={(e) => setExpenseFilter(e.target.value)}
                    placeholder="Type to filter..."
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>User:</label>
                  <input 
                    type="text" 
                    value={userFilter} 
                    onChange={(e) => setUserFilter(e.target.value)}
                    placeholder="Type user name..."
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button 
                    onClick={fetchExpenses}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              {expenseMessage && (
                <div style={{
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  borderRadius: '4px',
                  backgroundColor: expenseMessageType === 'error' ? '#f8d7da' : expenseMessageType === 'info' ? '#e7f3ff' : '#d4edda',
                  color: expenseMessageType === 'error' ? '#721c24' : expenseMessageType === 'info' ? '#1f4f7a' : '#155724',
                  border: `1px solid ${expenseMessageType === 'error' ? '#f5c6cb' : expenseMessageType === 'info' ? '#b6dcff' : '#c3e6cb'}`
                }}>
                  {expenseMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '70px' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'center', width: '40px' }}>★</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>User</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Expense</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Vendor</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Receipt</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Statement ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filteredExpenses = expenses.filter(expense => {
                        const expenseMatch = !expenseFilter || (expense.expense && expense.expense.toLowerCase().includes(expenseFilter.toLowerCase()));
                        const userName = getUserName(expense.userId).toLowerCase();
                        const userMatch = !userFilter || userName.includes(userFilter.toLowerCase());
                        return expenseMatch && userMatch;
                      });
                      
                      if (filteredExpenses.length === 0) {
                        return (
                          <tr>
                            <td colSpan="9" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                              No expenses found for the selected criteria
                            </td>
                          </tr>
                        );
                      }
                      
                      return filteredExpenses.map((expense) => {
                        const isEditing = editingExpenseId === expense.id;
                        return (
                        <tr key={expense.id} style={{ backgroundColor: expense.isHighlighted ? '#fff3cd' : 'white' }}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                              {isEditing ? (
                                <>
                                  <button onClick={() => saveExpense(expense)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                  <button onClick={() => setEditingExpenseId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                </>
                              ) : (
                                <>
                                  <button onClick={() => setEditingExpenseId(expense.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                  {!expense.id?.toString().startsWith('temp_') && (
                                    <button onClick={() => requestDeleteExpense(expense)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'center' }}>
                            <input type="checkbox" checked={!!expense.isHighlighted} onChange={async (e) => {
                              const endpoint = e.target.checked ? 'highlight' : 'unhighlight';
                              try {
                                const res = await fetch(`${API_BASE_URL}/expense/${expense.id}/${endpoint}`, {
                                  method: 'POST',
                                  headers: { 'session-id': currentUser?.sessionId }
                                });
                                if (res.ok) {
                                  const updated = await res.json();
                                  setExpenses(prev => prev.map(ex => ex.id === expense.id ? { ...ex, isHighlighted: updated.isHighlighted } : ex));
                                }
                              } catch (error) {
                                console.error('Error toggling highlight:', error);
                              }
                            }} />
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {isEditing ? <input type="date" value={expense.date || ''} onChange={(e) => updateExpenseField(expense.id, 'date', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : '')}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {getUserName(expense.userId)}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {isEditing ? <input type="text" value={expense.expense || ''} onChange={(e) => updateExpenseField(expense.id, 'expense', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.expense || '')}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {isEditing ? <input type="text" value={expense.vendor || ''} onChange={(e) => updateExpenseField(expense.id, 'vendor', e.target.value)} style={{ width: '100%', border: 'none' }} /> : (expense.vendor || '')}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {isEditing ? <input type="number" step="0.01" value={expense.total || ''} onChange={(e) => updateExpenseField(expense.id, 'total', e.target.value)} style={{ width: '100%', border: 'none' }} /> : `$${expense.total || 0}`}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'center' }}>
                            {isEditing ? (
                              <div>
                                <input
                                  type="file"
                                  accept=".pdf,.png,.jpg,.jpeg,.webp"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = '';
                                    if (file) handleExpenseReceiptChange(expense.id, file);
                                  }}
                                  style={{ width: '100%', border: 'none', fontSize: '12px', color: 'transparent' }}
                                />
                                <ReceiptExtractionStatus isLoading={extractingExpenseIds.includes(expense.id)} />
                                {expense.receiptFile && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Selected: {expense.receiptFile.name}</div>}
                                {!expense.receiptFile && (expense.receiptImage || expense.isAttachment) && <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>Current file attached</div>}
                              </div>
                            ) : (
                            (expense.receiptImage || expense.isAttachment) ? (
                              <span 
                                onClick={() => openExpenseReceipt(expense)}
                                style={{ cursor: 'pointer', fontSize: '16px' }}
                                title="Click to view receipt"
                              >
                                📎
                              </span>
                            ) : '')}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {expense.earningStatementId || ''}
                          </td>
                        </tr>
                      );});
                    })()}
                  </tbody>
                </table>
              </div>
              
              {(() => {
                const filteredExpenses = expenses.filter(expense => {
                  const expenseMatch = !expenseFilter || (expense.expense && expense.expense.toLowerCase().includes(expenseFilter.toLowerCase()));
                  const userName = getUserName(expense.userId).toLowerCase();
                  const userMatch = !userFilter || userName.includes(userFilter.toLowerCase());
                  return expenseMatch && userMatch;
                });
                return filteredExpenses.length > 0 && (
                  <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
                    Total expenses: {filteredExpenses.length} | Total amount: ${(filteredExpenses.reduce((sum, exp) => sum + (parseFloat(exp.total) || 0), 0)).toFixed(2)}
                  </div>
                );
              })()}
            </div>
          )}

          {activeTab === 'earnings' && (
            <div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button 
                    onClick={fetchEarningsStatements}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>User</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Start Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>End Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Expenses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsStatements.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No earnings statements found
                        </td>
                      </tr>
                    ) : (
                      earningsStatements.map((earnings) => (
                        <tr key={earnings.id} onClick={() => handleEarningsStatementClick(earnings)} style={{ cursor: 'pointer' }}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {getUserName(earnings.userId)}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {earnings.startDate ? new Date(earnings.startDate + 'T00:00:00').toLocaleDateString() : ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {earnings.endDate ? new Date(earnings.endDate + 'T00:00:00').toLocaleDateString() : ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            ${earnings.total || 0}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            ${earnings.expenses || 0}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {earningsStatements.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
                  Total earnings statements: {earningsStatements.length}
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={startDate} 
                    onChange={(e) => setStartDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={endDate} 
                    onChange={(e) => setEndDate(e.target.value)}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button 
                    onClick={fetchActivityLog}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: 'pointer' 
                    }}
                  >
                    Refresh
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.9rem' }}>Username:</label>
                  <input
                    type="text"
                    placeholder="Filter by username..."
                    value={activityUsernameFilter}
                    onChange={(e) => setActivityUsernameFilter(e.target.value)}
                    style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem', width: '200px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500', fontSize: '0.9rem' }}>Method:</label>
                  <select
                    value={activityMethodFilter}
                    onChange={(e) => setActivityMethodFilter(e.target.value)}
                    style={{ padding: '0.4rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.9rem' }}
                  >
                    <option value="">All Methods</option>
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                  </select>
                </div>
              </div>

              <div style={{ overflowX: 'auto', maxHeight: '500px', border: '1px solid #dee2e6' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Date/Time</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Username</th>
                      {currentUser?.user?.orgRole === 'superuser' && <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Action</th>}
                      {currentUser?.user?.orgRole === 'superuser' && <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Method</th>}
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Endpoint</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = activityLog.filter(log => {
                        const matchUser = !activityUsernameFilter || (log.username || '').toLowerCase().includes(activityUsernameFilter.toLowerCase());
                        const matchMethod = !activityMethodFilter || log.method === activityMethodFilter;
                        return matchUser && matchMethod;
                      });
                      return filtered.length === 0 ? (
                      <tr>
                        <td colSpan={currentUser?.user?.orgRole === 'superuser' ? 6 : 4} style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No activity log entries found for the selected filters
                        </td>
                      </tr>
                    ) : (
                      filtered.map((log) => (
                        <tr key={log.id}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {log.createdAt ? new Date(log.createdAt).toLocaleString() : ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {log.username || ''}
                          </td>
                          {currentUser?.user?.orgRole === 'superuser' && <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {log.action || ''}
                          </td>}
                          {currentUser?.user?.orgRole === 'superuser' && <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            <span style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.8rem',
                              backgroundColor: log.method === 'GET' ? '#d4edda' : log.method === 'POST' ? '#fff3cd' : log.method === 'PUT' ? '#cce5ff' : log.method === 'DELETE' ? '#f8d7da' : '#e9ecef',
                              color: log.method === 'GET' ? '#155724' : log.method === 'POST' ? '#856404' : log.method === 'PUT' ? '#004085' : log.method === 'DELETE' ? '#721c24' : '#495057'
                            }}>
                              {log.method || ''}
                            </span>
                          </td>}
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {log.endpoint || ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {cleanDescription(log.description)}
                          </td>
                        </tr>
                      ))
                    );
                    })()}
                  </tbody>
                </table>
              </div>
              
              {activityLog.length > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
                  {(() => {
                    const filtered = activityLog.filter(log => {
                      const matchUser = !activityUsernameFilter || (log.username || '').toLowerCase().includes(activityUsernameFilter.toLowerCase());
                      const matchMethod = !activityMethodFilter || log.method === activityMethodFilter;
                      return matchUser && matchMethod;
                    });
                    return `Showing ${filtered.length} of ${activityLog.length} activity log entries`;
                  })()} 
                </div>
              )}
            </div>
          )}

          {activeTab === 'quizzes' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2>Quizzes</h2>
                <button 
                  onClick={() => {
                    setSelectedQuiz(null);
                    setQuizFormData({ category: '', topic: '', name: '', status: 'active' });
                    setQuizQuestions([]);
                    setShowQuizDialog(true);
                  }}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Add New Quiz
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '120px' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Category</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Topic</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Name</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Status</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quizzes.length === 0 ? (
                      <tr>
                        <td colSpan="6" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No quizzes found
                        </td>
                      </tr>
                    ) : (
                      quizzes.map((quiz) => (
                        <tr key={quiz.id} onClick={() => { loadQuizWithQuestions(quiz.id); setShowQuizDialog(true); }} style={{ cursor: 'pointer' }}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <ActionIconButton action="edit" label={`Edit quiz ${quiz.name}`} onClick={() => { loadQuizWithQuestions(quiz.id); setShowQuizDialog(true); }} />
                              <ActionIconButton action="delete" label={`Delete quiz ${quiz.name}`} onClick={() => { setQuizToDelete(quiz); setShowQuizDeleteConfirm(true); }} />
                            </div>
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{quiz.category}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{quiz.topic}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{quiz.name}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            <span style={{ 
                              padding: '0.25rem 0.5rem', 
                              borderRadius: '4px', 
                              fontSize: '0.8rem',
                              backgroundColor: quiz.status === 'active' ? '#d4edda' : quiz.status === 'pending' ? '#fff3cd' : '#f8d7da',
                              color: quiz.status === 'active' ? '#155724' : quiz.status === 'pending' ? '#856404' : '#721c24'
                            }}>
                              {quiz.status || 'active'}
                            </span>
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{quiz.createdAt ? new Date(quiz.createdAt).toLocaleDateString() : ''}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'organizations' && currentUser?.user?.orgRole === 'superuser' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2>Organizations</h2>
                <button 
                  onClick={() => {
                    organizationDetailsRequest.current += 1;
                    setSelectedOrganization(null);
                    setOrganizationFormData({ name: '', base: '', type: '', subscribedFeatures: '', address: '', city: '', state: '', zip: '', notes: '', status: 'active', aiDpeEnabled: false });
                    setOrganizationUsers([]);
                    setOrganizationActiveTab('details');
                    setShowOrganizationDialog(true);
                  }}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Add New Organization
                </button>
              </div>

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Name</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Base</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Type</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>City, State</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Features</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Status</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {organizations.length === 0 ? (
                      <tr>
                        <td colSpan="8" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No organizations found
                        </td>
                      </tr>
                    ) : (
                      organizations.map((org) => (
                        <tr key={org.id} onClick={() => {
                          setSelectedOrganization(org);
                          setOrganizationFormData({
                            name: org.name || '',
                            status: org.status ?? null,
                            aiDpeEnabled: org.aiDpeEnabled === true,
                            base: org.base || '',
                            type: org.type || '',
                            subscribedFeatures: org.subscribedFeatures || '',
                            address: org.address || '',
                            city: org.city || '',
                            state: org.state || '',
                            zip: org.zip || '',
                            notes: org.notes || ''
                          });
                          fetchOrganizationDetails(org.id, true);
                          setOrganizationActiveTab('details');
                          setShowOrganizationDialog(true);
                        }} style={{ cursor: 'pointer' }}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }} onClick={(e) => e.stopPropagation()}>
                            <ActionIconButton action="edit" label={`Edit organization ${org.name}`} onClick={() => {
                              setSelectedOrganization(org);
                              setOrganizationFormData({
                                name: org.name || '',
                                status: org.status ?? null,
                                aiDpeEnabled: org.aiDpeEnabled === true,
                                base: org.base || '',
                                type: org.type || '',
                                subscribedFeatures: org.subscribedFeatures || '',
                                address: org.address || '',
                                city: org.city || '',
                                state: org.state || '',
                                zip: org.zip || '',
                                notes: org.notes || ''
                              });
                              fetchOrganizationDetails(org.id, true);
                              setOrganizationActiveTab('details');
                              setShowOrganizationDialog(true);
                            }} />
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.name}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.base}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.type}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.city}{org.state ? `, ${org.state}` : ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.subscribedFeatures}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.status || 'Not set'}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{org.createdAt ? new Date(org.createdAt).toLocaleDateString() : ''}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'reports' && (
            <div>
              <h2>Reports</h2>
              
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '2rem', alignItems: 'center' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date:</label>
                  <input 
                    type="date" 
                    value={reportDateRange.startDate} 
                    onChange={(e) => setReportDateRange({...reportDateRange, startDate: e.target.value})}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date:</label>
                  <input 
                    type="date" 
                    value={reportDateRange.endDate} 
                    onChange={(e) => setReportDateRange({...reportDateRange, endDate: e.target.value})}
                    style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
                <div style={{ alignSelf: 'flex-end' }}>
                  <button 
                    onClick={() => fetchSingleReport(activeReport)}
                    disabled={loadingReports}
                    style={{ 
                      padding: '0.5rem 1rem', 
                      backgroundColor: '#007bff', 
                      color: 'white', 
                      border: 'none', 
                      borderRadius: '4px', 
                      cursor: loadingReports ? 'not-allowed' : 'pointer',
                      opacity: loadingReports ? 0.6 : 1
                    }}
                  >
                    {loadingReports ? 'Loading...' : 'Generate Report'}
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
                {[
                  { key: 'flightHours', label: 'Flight Hours' },
                  { key: 'aircraftUtilization', label: 'Aircraft Utilization' },
                  { key: 'instructorActivity', label: 'Instructor Activity' },
                  { key: 'revenueSummary', label: 'Revenue Summary' },
                  { key: 'studentProgress', label: 'Student Progress' }
                ].map(report => (
                  <button
                    key={report.key}
                    onClick={() => setActiveReport(report.key)}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: activeReport === report.key ? '#007bff' : '#f8f9fa',
                      color: activeReport === report.key ? 'white' : '#495057',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.9rem'
                    }}
                  >
                    {report.label}
                  </button>
                ))}
              </div>

              {activeReport === 'flightHours' && (
                <div>
                  <h3>Flight Hours Report</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Aircraft</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Flights</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Hours</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Avg Duration</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.flightHours.map((item, index) => (
                            <tr key={index}>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.tail_number} ({item.make} {item.model})</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.total_flights}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{(parseFloat(item.total_hours) || 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{(parseFloat(item.avg_flight_duration) || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                      <h4>Total Hours by Aircraft</h4>
                      {reports.flightHours.map((item, index) => (
                        <div key={index} style={{ marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                            <span>{item.tail_number}</span>
                            <span>{(parseFloat(item.total_hours) || 0).toFixed(2)}h</span>
                          </div>
                          <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${(item.total_hours / Math.max(...reports.flightHours.map(i => i.total_hours))) * 100}%`, height: '100%', backgroundColor: '#007bff' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeReport === 'aircraftUtilization' && (
                <div>
                  <h3>Aircraft Utilization Report</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Aircraft</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Rate</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Bookings</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Hours Flown</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.aircraftUtilization.map((item, index) => (
                            <tr key={index}>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.tail_number} ({item.make} {item.model})</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>${(parseFloat(item.rate) || 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.bookings}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{(parseFloat(item.hours_flown) || 0).toFixed(2)}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>${(parseFloat(item.revenue) || 0).toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                      <h4>Revenue by Aircraft</h4>
                      {reports.aircraftUtilization.map((item, index) => (
                        <div key={index} style={{ marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                            <span>{item.tail_number}</span>
                            <span>${(parseFloat(item.revenue) || 0).toFixed(2)}</span>
                          </div>
                          <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${(item.revenue / Math.max(...reports.aircraftUtilization.map(i => i.revenue))) * 100}%`, height: '100%', backgroundColor: '#28a745' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeReport === 'instructorActivity' && (
                <div>
                  <h3>Instructor Activity Report</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                        <thead>
                          <tr style={{ backgroundColor: '#f8f9fa' }}>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Instructor</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Email</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Lessons</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Dual Given</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Hours</th>
                            <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Unique Students</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reports.instructorActivity.map((item, index) => (
                            <tr key={index}>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.first_name} {item.last_name}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.email}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.total_lessons_taught ?? item.total_lessons ?? 0}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{getInstructorActivityDualGiven(item).toFixed(2)}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{getInstructorActivityTotalHours(item).toFixed(2)}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.unique_students}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                      <h4>Hours by Instructor</h4>
                      {reports.instructorActivity.map((item, index) => (
                        <div key={index} style={{ marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                            <span>{item.first_name} {item.last_name}</span>
                            <span>{getInstructorActivityTotalHours(item).toFixed(2)}h</span>
                          </div>
                          <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${maxInstructorActivityHours > 0 ? (getInstructorActivityTotalHours(item) / maxInstructorActivityHours) * 100 : 0}%`, height: '100%', backgroundColor: '#17a2b8' }}></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeReport === 'revenueSummary' && (
                <div>
                  <h3>Revenue Summary Report</h3>
                  {reports.revenueSummary.length === 0 ? (
                    <div style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>No revenue data found</div>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '2rem' }}>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f8f9fa' }}>
                              <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Category</th>
                              <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Count</th>
                              <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Revenue</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reports.revenueSummary.map((item, index) => (
                              <tr key={index}>
                                <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.category}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.count}</td>
                                <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>${(parseFloat(item.revenue) || 0).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div style={{ marginTop: '1rem', fontSize: '1rem', fontWeight: 'bold' }}>
                          Total Revenue: ${reports.revenueSummary.reduce((sum, item) => sum + (parseFloat(item.revenue) || 0), 0).toFixed(2)}
                        </div>
                      </div>
                      <div style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '4px' }}>
                        <h4>Revenue Distribution</h4>
                        {reports.revenueSummary.map((item, index) => {
                          const colors = ['#007bff', '#28a745', '#ffc107', '#dc3545', '#6f42c1'];
                          const totalRevenue = reports.revenueSummary.reduce((sum, i) => sum + (parseFloat(i.revenue) || 0), 0);
                          const percentage = totalRevenue > 0 ? ((parseFloat(item.revenue) || 0) / totalRevenue * 100).toFixed(1) : '0.0';
                          return (
                            <div key={index} style={{ marginBottom: '0.5rem' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                                <span>{item.category}</span>
                                <span>{percentage}%</span>
                              </div>
                              <div style={{ width: '100%', height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
                                <div style={{ width: `${percentage}%`, height: '100%', backgroundColor: colors[index % colors.length] }}></div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeReport === 'studentProgress' && (
                <div>
                  <h3>Student Progress Report</h3>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Student</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Email</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Lessons</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Total Hours</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Last Lesson</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Lessons (30 days)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reports.studentProgress.map((item, index) => (
                          <tr key={index}>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.firstName} {item.lastName}</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.email}</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.totalLessons}</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{(parseFloat(item.totalHours) || 0).toFixed(2)}</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.lastLessonDate ? new Date(item.lastLessonDate).toLocaleDateString() : 'N/A'}</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{item.lessonsLast30Days}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          {activeTab === 'settings' && (
            <div>
              <h2>System Settings</h2>
              
              {settingsMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: settingsMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: settingsMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${settingsMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {settingsMessage}
                </div>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
                <div>
                  <h3>Organization Branding</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Organization Logo:</label>
                    <input type="file" accept="image/*" onChange={handleLogoImageChange} />
                    <div style={{ marginTop: '0.25rem', color: '#666', fontSize: '0.85rem' }}>Maximum file size: 100 KB</div>
                    {(localSettings.logoImage || localSettings.logoImageUrl) && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem' }}>
                        <img src={localSettings.logoImage || localSettings.logoImageUrl} alt="Organization logo preview" style={{ width: '88px', height: '88px', objectFit: 'contain' }} />
                        <button type="button" onClick={() => setLocalSettings((prev) => ({ ...prev, logoImage: '' }))} style={{ padding: '0.4rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: 'white', cursor: 'pointer' }}>Remove logo</button>
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="organization-website" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Organization Website:</label>
                    <input
                      id="organization-website"
                      type="url"
                      value={localSettings.website || ''}
                      onChange={(e) => setLocalSettings((prev) => ({ ...prev, website: e.target.value }))}
                      placeholder="https://example.com"
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>

                <div>
                  <h3>Airport Settings</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="airport-metar-code" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Airport METARs (airport codes to display METARs for)</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <input
                        id="airport-metar-code"
                        type="text"
                        value={newAirportCode}
                        onChange={(event) => {
                          setNewAirportCode(event.target.value.replace(/[^a-z0-9]/gi, '').slice(0, 4));
                          setAirportCodeError('');
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            addAirportCode();
                          }
                        }}
                        placeholder="e.g. KBOS"
                        maxLength={4}
                        autoCapitalize="characters"
                        aria-describedby={airportCodeError ? 'airport-metar-error' : undefined}
                        style={{ width: '10rem', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      />
                      <button type="button" onClick={addAirportCode} style={{ padding: '0.5rem 0.8rem', border: '1px solid #0d9488', borderRadius: '4px', color: '#0f766e', backgroundColor: 'white', cursor: 'pointer' }}>Add airport</button>
                    </div>
                    {airportCodeError && <div id="airport-metar-error" role="alert" style={{ marginTop: '0.35rem', color: '#b91c1c', fontSize: '0.85rem' }}>{airportCodeError}</div>}
                    <ul aria-label="Airport METAR codes" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '0.75rem 0 0', padding: 0, listStyle: 'none' }}>
                      {airportCodes.map(code => (
                        <li key={code} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.35rem 0.5rem', border: '1px solid #cbd5e1', borderRadius: '4px', backgroundColor: '#f8fafc' }}>
                          <span style={{ fontWeight: 600 }}>{code}</span>
                          <button type="button" aria-label={`Remove ${code}`} title={`Remove ${code}`} onClick={() => removeAirportCode(code)} style={{ padding: 0, border: 0, background: 'transparent', color: '#64748b', fontSize: '1.1rem', lineHeight: 1, cursor: 'pointer' }}>×</button>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div>
                  <h3>Wind Settings</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Caution Wind (knots):</label>
                    <input 
                      type="number" 
                      value={localSettings.cautionWind} 
                      onChange={(e) => setLocalSettings({...localSettings, cautionWind: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, cautionWind: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Max Wind (knots):</label>
                    <input 
                      type="number" 
                      value={localSettings.maxWind} 
                      onChange={(e) => setLocalSettings({...localSettings, maxWind: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, maxWind: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>

                <div>
                  <h3>Ceiling Settings</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Caution Ceiling (feet):</label>
                    <input 
                      type="number" 
                      value={localSettings.cautionCeiling} 
                      onChange={(e) => setLocalSettings({...localSettings, cautionCeiling: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, cautionCeiling: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Min Ceiling (feet):</label>
                    <input 
                      type="number" 
                      value={localSettings.minCeiling} 
                      onChange={(e) => setLocalSettings({...localSettings, minCeiling: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, minCeiling: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>

                <div>
                  <h3>Visibility Settings</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Caution Visibility (miles):</label>
                    <input 
                      type="number" 
                      value={localSettings.cautionVisibility} 
                      onChange={(e) => setLocalSettings({...localSettings, cautionVisibility: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, cautionVisibility: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Min Visibility (miles):</label>
                    <input 
                      type="number" 
                      value={localSettings.minVisibility} 
                      onChange={(e) => setLocalSettings({...localSettings, minVisibility: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, minVisibility: e.target.value === '' ? 0 : parseInt(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>

                <div>
                  <h3>Flight Reservation Hours</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Day Start (24-hour format):</label>
                    <select 
                      value={localSettings.flightReservationDayStart} 
                      onChange={(e) => setLocalSettings({...localSettings, flightReservationDayStart: parseInt(e.target.value)})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      {Array.from({length: 15}, (_, i) => i + 4).map(hour => (
                        <option key={hour} value={hour}>{hour}:00</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Day End (24-hour format):</label>
                    <select 
                      value={localSettings.flightReservationDayEnd} 
                      onChange={(e) => setLocalSettings({...localSettings, flightReservationDayEnd: parseInt(e.target.value)})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    >
                      {Array.from({length: 15}, (_, i) => i + 9).map(hour => (
                        <option key={hour} value={hour}>{hour}:00</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <h3>Simulator Settings</h3>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Simulator Rate ($/hour):</label>
                    <input 
                      type="number" 
                      step="0.01"
                      value={localSettings.simRate} 
                      onChange={(e) => setLocalSettings({...localSettings, simRate: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, simRate: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Instruction Owner Aircraft Fee ($/hour):</label>
                    <input 
                      type="number" 
                      step="0.01"
                      value={localSettings.instructionOwnerAircraftFee} 
                      onChange={(e) => setLocalSettings({...localSettings, instructionOwnerAircraftFee: e.target.value})}
                      onBlur={(e) => setLocalSettings({...localSettings, instructionOwnerAircraftFee: e.target.value === '' ? 0 : parseFloat(e.target.value) || 0})}
                      style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button 
                  onClick={saveSettings}
                  style={{ 
                    padding: '0.75rem 2rem', 
                    backgroundColor: '#007bff', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    fontSize: '1rem',
                    fontWeight: '500'
                  }}
                >
                  Save Settings
                </button>
                {(currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
                  <ActionIconButton action="delete" label="Delete this account"
                    onClick={() => {
                      setShowDeleteOrgDialog(true);
                      setDeleteOrgMessage('');
                    }}
                    disabled={loadingOrganization}
                  />
                )}
              </div>
            </div>
          )}
          {activeTab === 'featureRequests' && currentUser?.user?.orgRole === 'superuser' && (
            <div>
              <h2>Feature Requests</h2>
              {featureRequestMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: featureRequestMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: featureRequestMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${featureRequestMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {featureRequestMessage}
                </div>
              )}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Title</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Description</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Priority</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Status</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Created By</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {featureRequests.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No feature requests found
                        </td>
                      </tr>
                    ) : (
                      featureRequests.map((request) => {
                        const isEditing = editingAdminFeatureRequestId === request.id;
                        return (
                          <tr key={request.id}>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {isEditing ? (
                                  <>
                                    <button onClick={() => saveAdminFeatureRequest(request)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                    <button onClick={() => setEditingAdminFeatureRequestId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                  </>
                                ) : (
                                  <button onClick={() => setEditingAdminFeatureRequestId(request.id)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                )}
                              </div>
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? 
                                <input type="text" value={request.title || ''} onChange={(e) => updateAdminFeatureRequestField(request.id, 'title', e.target.value)} style={{ width: '100%', border: 'none' }} /> : 
                                (
                                  <span
                                    onClick={() => openFeatureRequestDialog(request)}
                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                    title="Open feature request details"
                                  >
                                    {request.title || ''}
                                  </span>
                                )
                              }
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', maxWidth: '300px', wordWrap: 'break-word' }}>
                              {isEditing ? 
                                <textarea value={request.description || ''} onChange={(e) => updateAdminFeatureRequestField(request.id, 'description', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> : 
                                getFeatureRequestDescriptionPreview(request.description)
                              }
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? (
                                <select value={request.priority || 'medium'} onChange={(e) => updateAdminFeatureRequestField(request.id, 'priority', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                  <option value="low">Low</option>
                                  <option value="medium">Medium</option>
                                  <option value="high">High</option>
                                  <option value="highest">Highest</option>
                                  <option value="critical">Critical</option>
                                </select>
                              ) : (
                                <span style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem',
                                  backgroundColor: request.priority === 'critical' ? '#450a0a' : request.priority === 'highest' ? '#721c24' : request.priority === 'high' ? '#f8d7da' : request.priority === 'medium' ? '#fff3cd' : '#d4edda',
                                  color: ['critical', 'highest'].includes(request.priority) ? 'white' : request.priority === 'high' ? '#721c24' : request.priority === 'medium' ? '#856404' : '#155724'
                                }}>
                                  {request.priority ? request.priority.charAt(0).toUpperCase() + request.priority.slice(1) : 'Medium'}
                                </span>
                              )}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? (
                                <select value={request.status || 'open'} onChange={(e) => updateAdminFeatureRequestField(request.id, 'status', e.target.value)} style={{ width: '100%', border: 'none' }}>
                                  <option value="open">Open</option>
                                  <option value="in_progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                  <option value="rejected">Rejected</option>
                                  <option value="duplicate">Duplicate</option>
                                </select>
                              ) : (
                                <span style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem',
                                  backgroundColor: request.status === 'completed' ? '#d4edda' : request.status === 'in_progress' ? '#cce5ff' : request.status === 'rejected' ? '#f8d7da' : '#fff3cd',
                                  color: request.status === 'completed' ? '#155724' : request.status === 'in_progress' ? '#004085' : request.status === 'rejected' ? '#721c24' : '#856404'
                                }}>
                                  {request.status ? request.status.replace('_', ' ') : 'open'}
                                </span>
                              )}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{request.createdByName} ({request.createdByEmail})</td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{request.createdAt ? new Date(request.createdAt).toLocaleDateString() : ''}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === 'announcements' && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h2>Announcements</h2>
                <button 
                  onClick={() => {
                    setSelectedAnnouncement(null);
                    setAnnouncementAudience('current');
                    setAnnouncementFormData({ message: '', startDatetime: '', endDatetime: '' });
                    setShowAnnouncementDialog(true);
                  }}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Create Announcement
                </button>
              </div>

              {announcementMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: announcementMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: announcementMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${announcementMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {announcementMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Message</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Start Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>End Date</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Organization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {announcements.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No announcements found
                        </td>
                      </tr>
                    ) : (
                      announcements.map((announcement) => (
                        <tr key={announcement.id}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            <ActionIconButton action="edit" label="Edit announcement"
                              onClick={() => {
                                setSelectedAnnouncement(announcement);
                                setAnnouncementFormData({
                                  message: announcement.message || '',
                                  startDatetime: announcement.startDatetime || '',
                                  endDatetime: announcement.endDatetime || ''
                                });
                                setShowAnnouncementDialog(true);
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => showAnnouncementDismissals(announcement)}
                              title="View users who dismissed this announcement"
                              aria-label="View users who dismissed this announcement"
                              style={{ marginLeft: '0.5rem', padding: '0.25rem 0.5rem', backgroundColor: 'transparent', color: '#495057', border: '1px solid #ced4da', borderRadius: '4px', cursor: 'pointer', fontSize: '16px' }}
                            >
                              👥
                            </button>
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', maxWidth: '300px', wordWrap: 'break-word' }}>
                            <div dangerouslySetInnerHTML={{ __html: announcement.message || '' }} />
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {announcement.startDatetime ? new Date(announcement.startDatetime).toLocaleString() : ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {announcement.endDatetime ? new Date(announcement.endDatetime).toLocaleString() : ''}
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                            {announcement.organizationName || (!announcement.orgId ? 'All Organizations' : '')}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === 'maneuvers' && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <h2 style={{ margin: 0 }}>Maneuvers</h2>
                  <select
                    value={selectedManeuverCategoryFilter}
                    onChange={(e) => setSelectedManeuverCategoryFilter(e.target.value)}
                    style={{ padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '210px' }}
                  >
                    <option value="">All Categories</option>
                    {maneuverCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    {hasUncategorizedManeuvers && <option value="__uncategorized__">Uncategorized</option>}
                  </select>
                </div>
                <button 
                  onClick={() => {
                    setSelectedManeuver(null);
                    const firstCat = [...new Set(maneuvers.map(m => m.category).filter(c => c && c.trim()))].sort()[0] || '';
                    setManeuverFormData({ name: '', category: firstCat, description: '', steps: '' });
                    setManeuverCategoryInput('');
                    setNewManeuverCategoryName('');
                    setShowManeuverDialog(true);
                  }}
                  style={{ 
                    padding: '0.5rem 1rem', 
                    backgroundColor: '#28a745', 
                    color: 'white', 
                    border: 'none', 
                    borderRadius: '4px', 
                    cursor: 'pointer' 
                  }}
                >
                  Add New Maneuver
                </button>
              </div>

              {maneuverMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: maneuverMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: maneuverMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${maneuverMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {maneuverMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '60px' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '120px' }}>Name</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '120px' }}>Category</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '240px' }}>Description</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Steps</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredManeuvers.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No maneuvers found for the selected category
                        </td>
                      </tr>
                    ) : (
                      filteredManeuvers.map((maneuver) => {
                        const isEditing = editingManeuverId === maneuver.id;
                        const existingCategories = [...new Set(maneuvers.map(m => m.category).filter(c => c && c.trim()))].sort();
                        return (
                          <tr key={maneuver.id}>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {isEditing ? (
                                  <>
                                    <button onClick={() => saveInlineManeuver(maneuver)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                    <button onClick={() => setEditingManeuverId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                  </>
                                ) : (
                                  <button onClick={() => {
                                    setEditingManeuverId(maneuver.id);
                                    const cats = [...new Set(maneuvers.map(m => m.category).filter(c => c && c.trim()))].sort();
                                    if (!maneuver.category && cats.length === 1) updateManeuverField(maneuver.id, 'category', cats[0]);
                                  }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                )}
                              </div>
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? 
                                <input type="text" value={maneuver.name || ''} onChange={(e) => updateManeuverField(maneuver.id, 'name', e.target.value)} style={{ width: '100%', border: 'none' }} /> : 
                                (maneuver.name || '')
                              }
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? (
                                <>
                                  <select value={existingCategories.includes(maneuver.category) ? maneuver.category : (existingCategories.length === 1 ? existingCategories[0] : (maneuver.category ? '__new__' : ''))} onChange={(e) => { if (e.target.value !== '__new__') updateManeuverField(maneuver.id, 'category', e.target.value); else updateManeuverField(maneuver.id, 'category', ''); }} style={{ width: '100%', border: 'none', marginBottom: '2px' }}>
                                    {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                    <option value="__new__">+ New category...</option>
                                  </select>
                                  {(!existingCategories.includes(maneuver.category) || maneuver.category === '') && (
                                    <input type="text" placeholder="Type new category" defaultValue={maneuver.category || ''} onKeyDown={(e) => { if (e.key === 'Enter') { updateManeuverField(maneuver.id, 'category', e.target.value); e.target.blur(); } }} onBlur={(e) => updateManeuverField(maneuver.id, 'category', e.target.value)} style={{ width: '100%', border: 'none', fontSize: '0.85rem' }} />
                                  )}
                                </>
                              ) : (maneuver.category || '')}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', maxWidth: '300px', wordWrap: 'break-word' }}>
                              {isEditing ? 
                                <textarea value={maneuver.description || ''} onChange={(e) => updateManeuverField(maneuver.id, 'description', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> : 
                                (maneuver.description || '')
                              }
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                              {isEditing ? 
                                <HtmlRichEditor
                                  value={maneuver.steps || ''}
                                  onChange={(next) => updateManeuverField(maneuver.id, 'steps', next)}
                                  minHeight="90px"
                                  fixedHeight={true}
                                  placeholder="Enter step-by-step instructions..."
                                /> : 
                                <div dangerouslySetInnerHTML={{ __html: maneuver.steps || '' }} />
                              }
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === 'requirements' && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <h2 style={{ margin: 0 }}>Requirements</h2>
                  <select
                    value={selectedRequirementCategoryFilter}
                    onChange={(e) => selectRequirementCategoryFilter(e.target.value)}
                    style={{ padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '210px' }}
                  >
                    <option value="">All Categories</option>
                    {requirementCategories.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                    {hasUncategorizedRequirements && <option value="__uncategorized__">Uncategorized</option>}
                  </select>
                  {selectedRequirementCategoryFilter && (
                    <select
                      value={selectedRequirementStageFilter}
                      onChange={(e) => setSelectedRequirementStageFilter(e.target.value)}
                      style={{ padding: '0.45rem 0.65rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '180px' }}
                    >
                      {requirementStages.length === 0 ? (
                        <option value="">No stages</option>
                      ) : requirementStages.map((stage) => (
                        <option key={stage} value={stage}>{stage}</option>
                      ))}
                    </select>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedRequirement(null);
                    const firstCat = [...new Set(requirements.map(r => r.category).filter(c => c && c.trim()))].sort()[0] || '';
                    const firstStage = firstCat ? (getRequirementStagesForCategory(firstCat)[0] || '') : '';
                    setRequirementFormData({ requirement: '', stage: firstStage, category: firstCat });
                    setRequirementCategoryInput('');
                    setNewRequirementCategoryName('');
                    setRequirementStageInput('');
                    setNewRequirementStageName('');
                    setShowRequirementDialog(true);
                  }}
                  style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                >
                  Add New Requirement
                </button>
              </div>

              {requirementMessage && (
                <div style={{
                  padding: '0.75rem',
                  marginBottom: '1rem',
                  borderRadius: '4px',
                  backgroundColor: requirementMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: requirementMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${requirementMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {requirementMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '60px' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Requirement</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '160px' }}>Category</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '160px' }}>Stage</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '120px' }}>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRequirements.length === 0 ? (
                      <tr>
                        <td colSpan="5" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                          No requirements found for the selected category
                        </td>
                      </tr>
                    ) : (
                      filteredRequirements.map((requirementItem) => {
                        const isEditing = editingRequirementId === requirementItem.id;
                        const existingCategories = [...new Set(requirements.map(r => r.category).filter(c => c && c.trim()))].sort();
                        const existingStages = getRequirementStagesForCategory((requirementItem.category || '').trim());
                        return (
                          <tr key={requirementItem.id}>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              <div style={{ display: 'flex', gap: '0.25rem' }}>
                                {isEditing ? (
                                  <>
                                    <button onClick={() => saveInlineRequirement(requirementItem)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>💾</button>
                                    <button onClick={() => setEditingRequirementId(null)} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                                  </>
                                ) : (
                                  <button onClick={() => {
                                    setEditingRequirementId(requirementItem.id);
                                    const cats = [...new Set(requirements.map(r => r.category).filter(c => c && c.trim()))].sort();
                                    if (!requirementItem.category && cats.length === 1) updateRequirementField(requirementItem.id, 'category', cats[0]);
                                  }} style={{ padding: '0.25rem', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>✏️</button>
                                )}
                              </div>
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ?
                                <textarea value={requirementItem.requirement || ''} onChange={(e) => updateRequirementField(requirementItem.id, 'requirement', e.target.value)} style={{ width: '100%', border: 'none', resize: 'vertical', minHeight: '60px' }} /> :
                                (requirementItem.requirement || '')
                              }
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? (
                                <>
                                  <select value={existingCategories.includes(requirementItem.category) ? requirementItem.category : (existingCategories.length === 1 ? existingCategories[0] : (requirementItem.category ? '__new__' : ''))} onChange={(e) => {
                                    if (e.target.value !== '__new__') {
                                      updateRequirementField(requirementItem.id, 'category', e.target.value);
                                      const nextStages = getRequirementStagesForCategory(e.target.value);
                                      if (nextStages.length > 0) updateRequirementField(requirementItem.id, 'stage', nextStages[0]);
                                    } else {
                                      updateRequirementField(requirementItem.id, 'category', '');
                                    }
                                  }} style={{ width: '100%', border: 'none', marginBottom: '2px' }}>
                                    <option value="">No category</option>
                                    {existingCategories.map(c => <option key={c} value={c}>{c}</option>)}
                                    <option value="__new__">+ New category...</option>
                                  </select>
                                  {(!existingCategories.includes(requirementItem.category) || requirementItem.category === '') && (
                                    <input type="text" placeholder="Type new category" defaultValue={requirementItem.category || ''} onKeyDown={(e) => { if (e.key === 'Enter') { updateRequirementField(requirementItem.id, 'category', e.target.value); e.target.blur(); } }} onBlur={(e) => updateRequirementField(requirementItem.id, 'category', e.target.value)} style={{ width: '100%', border: 'none', fontSize: '0.85rem' }} />
                                  )}
                                </>
                              ) : (requirementItem.category || '')}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                              {isEditing ? (
                                <>
                                  <select value={existingStages.includes(requirementItem.stage) ? requirementItem.stage : (requirementItem.stage ? '__new__' : '')} onChange={(e) => { if (e.target.value !== '__new__') updateRequirementField(requirementItem.id, 'stage', e.target.value); else updateRequirementField(requirementItem.id, 'stage', ''); }} style={{ width: '100%', border: 'none', marginBottom: '2px' }}>
                                    <option value="">Select stage...</option>
                                    {existingStages.map(stage => <option key={stage} value={stage}>{stage}</option>)}
                                    <option value="__new__">+ New stage...</option>
                                  </select>
                                  {(!existingStages.includes(requirementItem.stage) || requirementItem.stage === '') && (
                                    <input type="text" placeholder="Type new stage" defaultValue={requirementItem.stage || ''} onKeyDown={(e) => { if (e.key === 'Enter') { updateRequirementField(requirementItem.id, 'stage', e.target.value); e.target.blur(); } }} onBlur={(e) => updateRequirementField(requirementItem.id, 'stage', e.target.value)} style={{ width: '100%', border: 'none', fontSize: '0.85rem' }} />
                                  )}
                                </>
                              ) : (requirementItem.stage || '')}
                            </td>
                            <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{requirementItem.createdAt ? new Date(requirementItem.createdAt).toLocaleDateString() : ''}</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {activeTab === 'agreements' && (currentUser?.user?.orgRole === 'admin' || currentUser?.user?.orgRole === 'superuser') && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>{agreements.length} agreement{agreements.length !== 1 ? 's' : ''}</div>
                <button onClick={() => { setIsAddingAgreement(true); setNewAgreementRow({ name: '', file: null }); }} disabled={isAddingAgreement} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: isAddingAgreement ? 'not-allowed' : 'pointer', opacity: isAddingAgreement ? 0.6 : 1 }}>Add Agreement</button>
              </div>

              {agreementMessage && (
                <div style={{ padding: '0.75rem', marginBottom: '1rem', borderRadius: '4px', backgroundColor: agreementMessageType === 'success' ? '#d4edda' : '#f8d7da', color: agreementMessageType === 'success' ? '#155724' : '#721c24', border: `1px solid ${agreementMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}` }}>
                  {agreementMessage}
                </div>
              )}

              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '80px' }}>Actions</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Name</th>
                      <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left', width: '200px' }}>PDF File</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isAddingAgreement && (
                      <tr style={{ backgroundColor: '#e8f5e8' }}>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                          <button onClick={uploadAgreement} disabled={uploadingAgreement} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }}>{uploadingAgreement ? '⏳' : '💾'}</button>
                          <button onClick={() => { setIsAddingAgreement(false); setNewAgreementRow({ name: '', file: null }); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>❌</button>
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          <input type="text" value={newAgreementRow.name} onChange={(e) => setNewAgreementRow({ ...newAgreementRow, name: e.target.value })} placeholder="Agreement name" style={{ width: '100%', border: 'none', padding: '0.25rem' }} />
                        </td>
                        <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                          <input type="file" accept="application/pdf" onChange={(e) => setNewAgreementRow({ ...newAgreementRow, file: e.target.files[0] || null })} style={{ fontSize: '0.85rem' }} />
                        </td>
                      </tr>
                    )}
                    {agreements.length === 0 && !isAddingAgreement ? (
                      <tr><td colSpan="3" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>No agreements found</td></tr>
                    ) : (
                      agreements.map((agreement) => (
                        <tr key={agreement.id}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                            <button onClick={() => viewAgreement(agreement.id)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px', marginRight: '0.25rem' }} title="View PDF">📄</button>
                            <button onClick={() => { if (window.confirm('Delete this agreement?')) deleteAgreement(agreement.id); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '14px' }}>🗑️</button>
                          </td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{agreement.name || ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.75rem', fontSize: '0.85rem' }}>
                            <span onClick={() => viewAgreement(agreement.id)} style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}>agreement-{agreement.id}.pdf</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showAgreementPdfModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 3000 }}>
              <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.3)', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', backgroundColor: '#e6f3ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
                  <h3 style={{ margin: 0 }}>Agreement PDF</h3>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    <a href={viewingAgreementPdfUrl} download="agreement.pdf" style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', borderRadius: '4px', textDecoration: 'none', fontSize: '0.9rem' }}>Download</a>
                    <button onClick={() => { setShowAgreementPdfModal(false); URL.revokeObjectURL(viewingAgreementPdfUrl); setViewingAgreementPdfUrl(''); }} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px' }}>✕</button>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <iframe src={viewingAgreementPdfUrl} style={{ width: '100%', height: '100%', border: 'none' }} title="Agreement PDF" />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showQuizDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: '0' }}>{selectedQuiz ? 'Edit Quiz' : 'New Quiz'}</h3>
              <button onClick={() => setShowQuizDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ padding: '1rem', borderBottom: '1px solid #ccc' }}>
              {quizMessage && (
                <div style={{ 
                  padding: '0.75rem', 
                  marginBottom: '1rem', 
                  borderRadius: '4px', 
                  backgroundColor: quizMessageType === 'success' ? '#d4edda' : '#f8d7da',
                  color: quizMessageType === 'success' ? '#155724' : '#721c24',
                  border: `1px solid ${quizMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  {quizMessage}
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Category:</label>
                  <input type="text" value={quizFormData.category} onChange={(e) => setQuizFormData({...quizFormData, category: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Topic:</label>
                  <input type="text" value={quizFormData.topic} onChange={(e) => setQuizFormData({...quizFormData, topic: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Name:</label>
                  <input type="text" value={quizFormData.name} onChange={(e) => setQuizFormData({...quizFormData, name: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Status:</label>
                  <select value={quizFormData.status} onChange={(e) => setQuizFormData({...quizFormData, status: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <button onClick={saveQuiz} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Quiz Info</button>
                {selectedQuiz && <button onClick={addNewQuestion} style={{ padding: '0.5rem 1rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Add Question</button>}
              </div>
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', padding: '1rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h4 style={{ margin: 0 }}>Questions</h4>
              </div>
              
              {!selectedQuiz ? (
                <div style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>Save quiz info first to add questions</div>
              ) : quizQuestions.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#6c757d', padding: '2rem' }}>No questions yet</div>
              ) : (
                quizQuestions.map((q, index) => {
                  const isEditing = editingQuestionId === q.id;
                  const isLastQuestion = index === quizQuestions.length - 1;
                  return (
                    <div key={q.id} ref={isLastQuestion ? newQuestionRef : null} style={{ marginBottom: '1rem', padding: '1rem', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong>Question {index + 1}</strong>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          {isEditing ? (
                            <>
                              <button onClick={() => saveQuestion(q)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                              <button onClick={() => setEditingQuestionId(null)} style={{ padding: '0.25rem 0.5rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                            </>
                          ) : (
                            <>
                              <ActionIconButton action="edit" label={`Edit question ${index + 1}`} onClick={() => setEditingQuestionId(q.id)} />
                              <ActionIconButton action="delete" label={`Delete question ${index + 1}`} onClick={() => { if (window.confirm('Delete this question?')) deleteQuestion(q.id); }} />
                            </>
                          )}
                        </div>
                      </div>
                      {isEditing ? (
                        <div>
                          <textarea value={q.question} onChange={(e) => updateQuestionField(q.id, 'question', e.target.value)} placeholder="Question" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minHeight: '60px' }} />
                          <input type="text" value={q.choiceA} onChange={(e) => updateQuestionField(q.id, 'choiceA', e.target.value)} placeholder="Choice A" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                          <input type="text" value={q.choiceB} onChange={(e) => updateQuestionField(q.id, 'choiceB', e.target.value)} placeholder="Choice B" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                          <input type="text" value={q.choiceC} onChange={(e) => updateQuestionField(q.id, 'choiceC', e.target.value)} placeholder="Choice C" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                          <input type="text" value={q.choiceD} onChange={(e) => updateQuestionField(q.id, 'choiceD', e.target.value)} placeholder="Choice D" style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                          <div style={{ marginBottom: '0.5rem' }}>
                            <label style={{ marginRight: '1rem', fontWeight: '500' }}>Correct Answer:</label>
                            <select value={q.correctAnswer} onChange={(e) => updateQuestionField(q.id, 'correctAnswer', e.target.value)} style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                              <option value="A">A</option>
                              <option value="B">B</option>
                              <option value="C">C</option>
                              <option value="D">D</option>
                            </select>
                          </div>
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Image 1:</label>
                              <input type="file" accept="image/*" onChange={(e) => updateQuestionField(q.id, 'image1File', e.target.files[0])} style={{ width: '100%', padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem' }} />
                            </div>
                            <div>
                              <label style={{ display: 'block', marginBottom: '0.25rem', fontWeight: '500' }}>Image 2:</label>
                              <input type="file" accept="image/*" onChange={(e) => updateQuestionField(q.id, 'image2File', e.target.files[0])} style={{ width: '100%', padding: '0.25rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.8rem' }} />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div>
                          <div style={{ marginBottom: '0.5rem' }}>{q.question}</div>
                          <div style={{ fontSize: '0.9rem', color: '#495057' }}>
                            <div>A: {q.choiceA}</div>
                            <div>B: {q.choiceB}</div>
                            <div>C: {q.choiceC}</div>
                            <div>D: {q.choiceD}</div>
                            <div style={{ marginTop: '0.5rem', fontWeight: 'bold', color: '#28a745' }}>Correct: {q.correctAnswer} - {q[`choice${q.correctAnswer}`]}</div>
                            {(q.image1 || q.image2) && (
                              <div style={{ marginTop: '0.5rem' }}>
                                {q.image1 && (
                                  <span 
                                    onClick={() => {
                                      setSelectedImage(q.image1);
                                      setShowImageModal(true);
                                    }}
                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline', marginRight: '1rem' }}
                                  >
                                    View Image 1
                                  </span>
                                )}
                                {q.image2 && (
                                  <span 
                                    onClick={() => {
                                      setSelectedImage(q.image2);
                                      setShowImageModal(true);
                                    }}
                                    style={{ cursor: 'pointer', color: '#007bff', textDecoration: 'underline' }}
                                  >
                                    View Image 2
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowQuizDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Earnings Statement Details Modal */}
      {showEarningsDetailsDialog && selectedEarningsStatement && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '900px', height: '600px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
              <h3 style={{ textAlign: 'center', margin: '0', padding: '1rem' }}>Earnings Statement Details ({selectedEarningsStatement.startDate} - {selectedEarningsStatement.endDate})</h3>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('workLessons')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'workLessons' ? '1px solid #ffe6f0' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'workLessons' ? 'bold' : 'normal',
                marginRight: '2px',
                fontSize: '0.8rem'
              }}>Work Lessons</button>
              <button type="button" onClick={() => setEarningsDetailsActiveTab('expenses')} style={{ 
                padding: '0.5rem 0.75rem', 
                backgroundColor: earningsDetailsActiveTab === 'expenses' ? '#fff0e6' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: earningsDetailsActiveTab === 'expenses' ? '1px solid #fff0e6' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: earningsDetailsActiveTab === 'expenses' ? 'bold' : 'normal',
                fontSize: '0.8rem'
              }}>Expenses</button>
            </div>
            
            <div style={{ height: '450px', overflow: 'auto', backgroundColor: earningsDetailsActiveTab === 'workLessons' ? '#ffe6f0' : '#fff0e6', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              {earningsDetailsActiveTab === 'workLessons' && (
                <div>
                  <h4>Work Lessons (Count: {earningsWorkLessons.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Start Time</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Client Name</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Dual Given</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Ground Instruction</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsWorkLessons.map((lesson, index) => (
                          <tr key={lesson.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.date ? new Date(lesson.date + 'T00:00:00').toLocaleDateString() : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.startTime || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.clientName || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.dualGiven || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.groundInstruction || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {lesson.total || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              
              {earningsDetailsActiveTab === 'expenses' && (
                <div>
                  <h4>Expenses (Count: {earningsExpenses.length})</h4>
                  <div style={{ overflow: 'auto', maxHeight: '350px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Date</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Expense</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Vendor</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Total</th>
                          <th style={{ border: '1px solid #ddd', padding: '0.5rem', textAlign: 'left' }}>Receipt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {earningsExpenses.map((expense, index) => (
                          <tr key={expense.id || index}>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.date ? new Date(expense.date + 'T00:00:00').toLocaleDateString() : ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.expense || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.vendor || ''}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              ${expense.total || 0}
                            </td>
                            <td style={{ border: '1px solid #ddd', padding: '0.5rem' }}>
                              {expense.receiptImage ? (
                                <span 
                                  onClick={() => openExpenseReceipt(expense)}
                                  style={{ cursor: 'pointer', fontSize: '16px' }}
                                  title="Click to view receipt"
                                >
                                  📎
                                </span>
                              ) : ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => setShowEarningsDetailsDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal */}
      {showReceiptModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Receipt</h3>
              <button onClick={closeReceiptModal} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center', width: '100%', height: '100%' }}>
              {isLoadingReceipt ? (
                <div style={{ padding: '2rem', color: '#666' }}>Loading receipt...</div>
              ) : receiptModalMessage ? (
                <div style={{ padding: '2rem', color: '#721c24' }}>{receiptModalMessage}</div>
              ) : selectedReceiptType === 'pdf' ? (
                <iframe src={selectedReceiptImage} style={{ width: '100%', height: '75vh', border: 'none' }} title="Receipt PDF" />
              ) : (
                <img src={selectedReceiptImage} alt="Receipt" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', maxWidth: '90vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Question Image</h3>
              <button onClick={() => setShowImageModal(false)} style={{ backgroundColor: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', padding: '0.25rem' }}>✕</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', textAlign: 'center' }}>
              <img src={selectedImage} alt="Question" style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }} />
            </div>
          </div>
        </div>
      )}

      {/* Organization Dialog */}
      {showOrganizationDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '800px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: '0' }}>{selectedOrganization ? 'Edit Organization' : 'New Organization'}</h3>
              <button onClick={() => setShowOrganizationDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ display: 'flex', backgroundColor: '#f8f9fa', paddingLeft: '1rem', paddingRight: '1rem', paddingTop: '1rem' }}>
              <button type="button" onClick={() => setOrganizationActiveTab('details')} style={{ 
                padding: '0.5rem 1rem', 
                backgroundColor: organizationActiveTab === 'details' ? '#e6f7ff' : '#e9ecef', 
                color: '#495057',
                border: '1px solid #ccc', 
                borderBottom: organizationActiveTab === 'details' ? '1px solid #e6f7ff' : '1px solid #ccc',
                borderTopLeftRadius: '8px',
                borderTopRightRadius: '8px',
                cursor: 'pointer',
                fontWeight: organizationActiveTab === 'details' ? 'bold' : 'normal',
                marginRight: '2px'
              }}>Details</button>
              {selectedOrganization && (
                <button type="button" onClick={() => {
                  setOrganizationActiveTab('users');
                  if (organizationUsers.length === 0) {
                    fetchOrganizationDetails(selectedOrganization.id);
                  }
                }} style={{ 
                  padding: '0.5rem 1rem', 
                  backgroundColor: organizationActiveTab === 'users' ? '#f0f8f0' : '#e9ecef', 
                  color: '#495057',
                  border: '1px solid #ccc', 
                  borderBottom: organizationActiveTab === 'users' ? '1px solid #f0f8f0' : '1px solid #ccc',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: organizationActiveTab === 'users' ? 'bold' : 'normal'
                }}>Users</button>
              )}
            </div>
            
            <div style={{ flex: 1, overflow: 'auto', backgroundColor: organizationActiveTab === 'users' ? '#f0f8f0' : '#e6f7ff', border: '1px solid #ccc', borderTop: 'none', padding: '2rem' }}>
              {organizationActiveTab === 'details' && (
                <div>
                  {organizationMessage && (
                    <div style={{ 
                      padding: '0.75rem', 
                      marginBottom: '1rem', 
                      borderRadius: '4px', 
                      backgroundColor: organizationMessageType === 'success' ? '#d4edda' : '#f8d7da',
                      color: organizationMessageType === 'success' ? '#155724' : '#721c24',
                      border: `1px solid ${organizationMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                    }}>
                      {organizationMessage}
                    </div>
                  )}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Name:</label>
                      <input type="text" value={organizationFormData.name} onChange={(e) => setOrganizationFormData({...organizationFormData, name: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Base Airport:</label>
                      <input type="text" value={organizationFormData.base} onChange={(e) => setOrganizationFormData({...organizationFormData, base: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Type:</label>
                      <select value={organizationFormData.type} onChange={(e) => setOrganizationFormData({...organizationFormData, type: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                        <option value="">Select Type</option>
                        <option value="Flight School">Flight School</option>
                        <option value="Independent Flight Instructor">Independent Flight Instructor</option>
                        <option value="Flight Club">Flight Club</option>
                      </select>
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Subscribed Features:</label>
                      <div style={{ border: '1px solid #ccc', borderRadius: '4px', padding: '0.5rem', minHeight: '38px' }}>
                        {['All', 'Flight Schedule', 'Events', 'Training'].map(feature => {
                          const selectedFeatures = organizationFormData.subscribedFeatures ? organizationFormData.subscribedFeatures.split(',').map(f => f.trim()) : [];
                          const isSelected = selectedFeatures.includes(feature);
                          return (
                            <label key={feature} style={{ display: 'inline-block', marginRight: '1rem', cursor: 'pointer' }}>
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={(e) => {
                                  const currentFeatures = organizationFormData.subscribedFeatures ? organizationFormData.subscribedFeatures.split(',').map(f => f.trim()).filter(f => f) : [];
                                  let newFeatures;
                                  if (e.target.checked) {
                                    newFeatures = [...currentFeatures, feature];
                                  } else {
                                    newFeatures = currentFeatures.filter(f => f !== feature);
                                  }
                                  setOrganizationFormData({...organizationFormData, subscribedFeatures: newFeatures.join(', ')});
                                }}
                                style={{ marginRight: '0.25rem' }}
                              />
                              {feature}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label htmlFor="organization-status" style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Status:</label>
                    <select id="organization-status" value={organizationFormData.status ?? ''} onChange={event => {
                      organizationDetailsRequest.current += 1;
                      setOrganizationFormData({ ...organizationFormData, status: event.target.value });
                    }} style={{ width: 'auto', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                      {!organizationFormData.status && <option value="" disabled>Not set</option>}
                      {organizationFormData.status && !['active', 'inactive'].includes(organizationFormData.status) && <option value={organizationFormData.status}>{organizationFormData.status}</option>}
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '0.25rem' }}>Only active organizations allow members to sign in.</small>
                  </div>
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Address:</label>
                    <input type="text" value={organizationFormData.address} onChange={(e) => setOrganizationFormData({...organizationFormData, address: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>City:</label>
                      <input type="text" value={organizationFormData.city} onChange={(e) => setOrganizationFormData({...organizationFormData, city: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>State:</label>
                      <input type="text" value={organizationFormData.state} onChange={(e) => setOrganizationFormData({...organizationFormData, state: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>ZIP:</label>
                      <input type="text" value={organizationFormData.zip} onChange={(e) => setOrganizationFormData({...organizationFormData, zip: e.target.value})} style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }} />
                    </div>
                  </div>
                  <div style={{ marginBottom: '2rem' }}>
                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Notes:</label>
                    <textarea value={organizationFormData.notes} onChange={(e) => setOrganizationFormData({...organizationFormData, notes: e.target.value})} rows="3" style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }} />
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                      <input type="checkbox" checked={organizationFormData.aiDpeEnabled} onChange={event => setOrganizationFormData({ ...organizationFormData, aiDpeEnabled: event.target.checked })} />
                      Enable AI DPE for this organization
                    </label>
                  </div>
                </div>
              )}
              
              {organizationActiveTab === 'users' && selectedOrganization && (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h4>Organization Users</h4>
                    <button 
                      onClick={() => {
                        setSelectedOrgUser(null);
                        setShowOrgUserDialog(false);
                        setTimeout(() => {
                          setShowOrgUserDialog(true);
                        }, 0);
                      }}
                      style={{ 
                        padding: '0.5rem 1rem', 
                        backgroundColor: '#28a745', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '4px', 
                        cursor: 'pointer' 
                      }}
                    >
                      Add User
                    </button>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', fontSize: '0.9rem' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa' }}>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Actions</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Name</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Username</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Email</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Role</th>
                          <th style={{ border: '1px solid #dee2e6', padding: '0.75rem', textAlign: 'left' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {organizationUsers.length === 0 ? (
                          <tr>
                            <td colSpan="6" style={{ border: '1px solid #dee2e6', padding: '2rem', textAlign: 'center', color: '#6c757d' }}>
                              No users found for this organization
                            </td>
                          </tr>
                        ) : (
                          organizationUsers.map((user) => (
                            <tr key={user.id}>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <ActionIconButton action="edit" label={`Edit ${user.firstName} ${user.lastName}`}
                                    onClick={() => {
                                      setSelectedOrgUser(user);
                                      setShowOrgUserDialog(true);
                                    }}
                                  />
                                  <button 
                                    onClick={() => {
                                      if (window.confirm(`Are you sure you want to remove ${user.firstName} ${user.lastName} from this organization?`)) {
                                        removeUserFromOrganization(user.id);
                                      }
                                    }}
                                    style={{ padding: '0.25rem 0.5rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              </td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{user.firstName} {user.lastName}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{user.username}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{user.email}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>{user.orgRole}</td>
                              <td style={{ border: '1px solid #dee2e6', padding: '0.75rem' }}>
                                <span style={{ 
                                  padding: '0.25rem 0.5rem', 
                                  borderRadius: '4px', 
                                  fontSize: '0.8rem',
                                  backgroundColor: user.status === 'active' ? '#d4edda' : '#f8d7da',
                                  color: user.status === 'active' ? '#155724' : '#721c24'
                                }}>
                                  {user.status}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              {organizationActiveTab === 'details' && (
                <button onClick={saveOrganization} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', marginRight: '1rem' }}>Save Organization</button>
              )}
              <button onClick={() => setShowOrganizationDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Organization User Dialog */}
      <UserDialog 
        showDialog={showOrgUserDialog}
        onClose={() => {
          setShowOrgUserDialog(false);
          setSelectedOrgUser(null);
        }}
        userData={selectedOrgUser}
        onSave={async (userData) => {
          try {
            const payload = {
              ...userData,
              orgId: selectedOrganization?.id
            };
            let requestBody = JSON.stringify(payload);
            const requestHeaders = { 'Content-Type': 'application/json', 'session-id': currentUser?.sessionId };
            if (userData.profileImage) {
              const multipartData = new FormData();
              Object.entries(payload).forEach(([key, value]) => {
                if (value === undefined || value === null || key === 'profileImage' || key === 'profileImageUrl' || key === 'confirmPassword') return;
                const serializedValue = key === 'aircraftList'
                  ? JSON.stringify(value)
                  : ['isInstructor', 'isReferralRestricted', 'popupQuestionsOptOut'].includes(key)
                    ? String(value === true || value === 1 || value === '1' || value === 'true')
                    : value;
                multipartData.append(key, serializedValue);
              });
              multipartData.append('profileImage', userData.profileImage);
              requestBody = multipartData;
              delete requestHeaders['Content-Type'];
            }
            const response = await fetch(`${API_BASE_URL}/user`, {
              method: 'POST',
              headers: requestHeaders,
              body: requestBody
            });
            
            if (response.ok) {
              let savedUser = {};
              try { savedUser = await response.json(); } catch { /* Some user save responses may be empty. */ }
              setTimeout(() => {
                setShowOrgUserDialog(false);
                setSelectedOrgUser(null);
                if (selectedOrganization) {
                  fetchOrganizationDetails(selectedOrganization.id);
                }
              }, 2000);
              return { success: true, userId: savedUser.id || savedUser.userId || userData.id, message: 'User saved successfully' };
            } else {
              const errorText = await response.text();
              return { success: false, message: `Error: ${errorText}` };
            }
          } catch (error) {
            return { success: false, message: `Error: ${error.message}` };
          }
        }}
        title={selectedOrgUser ? `Edit User - ${selectedOrgUser.firstName} ${selectedOrgUser.lastName}` : 'Add New User'}
        organizationId={selectedOrganization?.id}
        style={{ zIndex: 3000 }}
        key={selectedOrgUser?.id || 'new'}
      />

      {/* Maneuver Dialog */}
      {showManeuverDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '500px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: '0' }}>{selectedManeuver ? 'Edit Maneuver' : 'Add New Maneuver'}</h3>
              <button onClick={() => setShowManeuverDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ padding: '2rem', overflowY: 'auto', overflowX: 'hidden', minWidth: 0 }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Name:</label>
                <input 
                  type="text" 
                  value={maneuverFormData.name} 
                  onChange={(e) => setManeuverFormData({...maneuverFormData, name: e.target.value})} 
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Category:</label>
                <select
                  value={maneuverCategoryInput === '__new__' ? '__new__' : maneuverFormData.category}
                  onChange={(e) => {
                    if (e.target.value === '__new__') {
                      setManeuverCategoryInput('__new__');
                      setManeuverFormData({...maneuverFormData, category: ''});
                      setNewManeuverCategoryName('');
                    } else {
                      setManeuverCategoryInput('');
                      setNewManeuverCategoryName('');
                      setManeuverFormData({...maneuverFormData, category: e.target.value});
                    }
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginBottom: maneuverCategoryInput === '__new__' ? '0.5rem' : '0' }}
                >
                  {[...new Set(maneuvers.map(m => m.category).filter(c => c && c.trim()))].sort().map(c => <option key={c} value={c}>{c}</option>)}
                  <option value="__new__">+ Add new category...</option>
                </select>
                {maneuverCategoryInput === '__new__' && (
                  <input
                    type="text"
                    value={newManeuverCategoryName}
                    onChange={(e) => setNewManeuverCategoryName(e.target.value)}
                    placeholder="Enter new category name"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const nextCategory = (newManeuverCategoryName || '').trim();
                        if (nextCategory) {
                          setManeuverFormData({ ...maneuverFormData, category: nextCategory });
                          setManeuverCategoryInput('');
                        }
                      }
                    }}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginTop: '0.5rem' }}
                    autoFocus
                  />
                )}
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Description:</label>
                <textarea 
                  value={maneuverFormData.description} 
                  onChange={(e) => setManeuverFormData({...maneuverFormData, description: e.target.value})} 
                  rows="3"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                />
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Steps:</label>
                <HtmlRichEditor
                  value={maneuverFormData.steps}
                  onChange={(next) => setManeuverFormData({ ...maneuverFormData, steps: next })}
                  minHeight="120px"
                  fixedHeight={true}
                  placeholder="Enter step-by-step instructions..."
                />
              </div>
              
              <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button onClick={saveManeuver} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                <button onClick={() => setShowManeuverDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showRequirementDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '620px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: '0' }}>{selectedRequirement ? 'Edit Requirement' : 'New Requirement'}</h3>
              <button onClick={() => setShowRequirementDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>

            <div style={{ padding: '2rem', overflow: 'auto' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Requirement:</label>
                <textarea
                  value={requirementFormData.requirement}
                  onChange={(e) => setRequirementFormData({ ...requirementFormData, requirement: e.target.value })}
                  rows="4"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Category:</label>
                <select
                  value={requirementCategoryInput || (requirementCategories.includes(requirementFormData.category) ? requirementFormData.category : (requirementFormData.category ? '__new__' : ''))}
                  onChange={(e) => {
                    const value = e.target.value;
                    setRequirementCategoryInput(value);
                    if (value === '__new__') {
                      setNewRequirementCategoryName(requirementCategories.includes(requirementFormData.category) ? '' : (requirementFormData.category || ''));
                      setRequirementFormData(prev => ({ ...prev, category: '', stage: '' }));
                      setRequirementStageInput('__new__');
                      setNewRequirementStageName('');
                    } else {
                      const firstStage = value ? (getRequirementStagesForCategory(value)[0] || '') : '';
                      setNewRequirementCategoryName('');
                      setRequirementStageInput('');
                      setNewRequirementStageName('');
                      setRequirementFormData(prev => ({ ...prev, category: value, stage: firstStage }));
                    }
                  }}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="">No category</option>
                  {requirementCategories.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                  <option value="__new__">+ New category...</option>
                </select>
                {requirementCategoryInput === '__new__' && (
                  <input
                    type="text"
                    placeholder="New category name"
                    value={newRequirementCategoryName}
                    onChange={(e) => setNewRequirementCategoryName(e.target.value)}
                    onBlur={(e) => {
                      const nextCategory = e.target.value.trim();
                      if (nextCategory) {
                        setRequirementFormData(prev => ({ ...prev, category: nextCategory, stage: '' }));
                        setRequirementCategoryInput('');
                        setRequirementStageInput('__new__');
                      }
                    }}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginTop: '0.5rem' }}
                    autoFocus
                  />
                )}
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Stage:</label>
                {(() => {
                  const dialogStages = getRequirementStagesForCategory((requirementFormData.category || '').trim());
                  return (
                    <>
                      <select
                        value={requirementStageInput || (dialogStages.includes(requirementFormData.stage) ? requirementFormData.stage : (requirementFormData.stage ? '__new__' : ''))}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRequirementStageInput(value);
                          if (value === '__new__') {
                            setNewRequirementStageName(dialogStages.includes(requirementFormData.stage) ? '' : (requirementFormData.stage || ''));
                            setRequirementFormData(prev => ({ ...prev, stage: '' }));
                          } else {
                            setNewRequirementStageName('');
                            setRequirementFormData(prev => ({ ...prev, stage: value }));
                          }
                        }}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      >
                        <option value="">Select stage...</option>
                        {dialogStages.map((stage) => (
                          <option key={stage} value={stage}>{stage}</option>
                        ))}
                        <option value="__new__">+ New stage...</option>
                      </select>
                      {requirementStageInput === '__new__' && (
                        <input
                          type="text"
                          placeholder="New stage name"
                          value={newRequirementStageName}
                          onChange={(e) => setNewRequirementStageName(e.target.value)}
                          onBlur={(e) => {
                            const nextStage = e.target.value.trim();
                            if (nextStage) {
                              setRequirementFormData(prev => ({ ...prev, stage: nextStage }));
                              setRequirementStageInput('');
                            }
                          }}
                          style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', marginTop: '0.5rem' }}
                          autoFocus
                        />
                      )}
                    </>
                  );
                })()}
              </div>

              <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button onClick={saveRequirement} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                <button onClick={() => setShowRequirementDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAnnouncementDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', width: '700px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: '0' }}>{selectedAnnouncement ? 'Edit Announcement' : 'Create Announcement'}</h3>
              <button onClick={() => setShowAnnouncementDialog(false)} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            
            <div style={{ padding: '2rem' }}>
              {currentUser?.user?.orgRole === 'superuser' && !selectedAnnouncement && <label style={{ display: 'grid', gap: '0.5rem', marginBottom: '1rem', fontWeight: 500 }}>Audience
                <select aria-label="Announcement audience" value={announcementAudience} onChange={event => setAnnouncementAudience(event.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}>
                  <option value="current">Current organization</option>
                  <option value="all">All organizations</option>
                </select>
              </label>}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Message:</label>
                <HtmlRichEditor
                  value={announcementFormData.message}
                  onChange={(next) => setAnnouncementFormData({ ...announcementFormData, message: next })}
                  minHeight="140px"
                  fixedHeight={true}
                  placeholder="Enter announcement message..."
                />
              </div>
              
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Start Date & Time:</label>
                <input 
                  type="datetime-local" 
                  value={announcementFormData.startDatetime} 
                  onChange={(e) => setAnnouncementFormData({...announcementFormData, startDatetime: e.target.value})} 
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
              
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>End Date & Time:</label>
                <input 
                  type="datetime-local" 
                  value={announcementFormData.endDatetime} 
                  onChange={(e) => setAnnouncementFormData({...announcementFormData, endDatetime: e.target.value})} 
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
              
              <div style={{ textAlign: 'center', display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                <button onClick={saveAnnouncement} style={{ padding: '0.5rem 1rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save</button>
                <button onClick={() => setShowAnnouncementDialog(false)} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showAnnouncementDismissalsDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ backgroundColor: 'white', padding: '0', borderRadius: '8px', boxShadow: '0 4px 16px rgba(0,0,0,0.2)', width: '760px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem' }}>
              <h3 style={{ textAlign: 'center', margin: 0 }}>Announcement Dismissals</h3>
              <button onClick={() => { setShowAnnouncementDismissalsDialog(false); setDismissedAnnouncement(null); }} style={{ position: 'absolute', top: '0.5rem', right: '0.5rem', background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #dee2e6', color: '#495057', maxHeight: '100px', overflow: 'auto' }}>
              <div dangerouslySetInnerHTML={{ __html: dismissedAnnouncement?.message || '' }} />
            </div>
            <div style={{ padding: '1rem', overflow: 'auto' }}>
              {dismissedAnnouncementLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>Loading users...</div>
              ) : dismissedAnnouncementError ? (
                <div style={{ padding: '1rem', color: '#721c24', backgroundColor: '#f8d7da', border: '1px solid #f5c6cb', borderRadius: '4px' }}>{dismissedAnnouncementError}</div>
              ) : dismissedAnnouncementUsers.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6c757d' }}>No users have dismissed this announcement.</div>
              ) : (
                <div style={{ maxHeight: '440px', overflow: 'auto', border: '1px solid #dee2e6' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Name</th>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Username</th>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Email</th>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Role</th>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Status</th>
                        <th style={{ border: '1px solid #dee2e6', padding: '0.65rem', textAlign: 'left' }}>Dismissed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dismissedAnnouncementUsers.map((user) => (
                        <tr key={user.id || user.userId}>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{`${user.firstName || ''} ${user.lastName || ''}`.trim()}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{user.username || ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{user.email || ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{user.orgRole || ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{user.status || ''}</td>
                          <td style={{ border: '1px solid #dee2e6', padding: '0.65rem' }}>{formatDismissedAtLocal(user.dismissedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={{ textAlign: 'center', padding: '1rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={() => { setShowAnnouncementDismissalsDialog(false); setDismissedAnnouncement(null); }} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      {showQuizDeleteConfirm && quizToDelete && (
        <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', 
                     backgroundColor: '#fff', padding: '0', border: '1px solid #ccc', zIndex: 1000, width: '400px', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
          <div style={{ padding: '1rem', backgroundColor: '#dc3545', color: 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}>
            <h3 style={{ margin: '0', textAlign: 'center' }}>Confirm Delete</h3>
          </div>
          <div style={{ padding: '2rem' }}>
            <p style={{ margin: '0 0 1.5rem 0', textAlign: 'center' }}>
              Are you sure you want to delete the quiz <strong>{quizToDelete.name}</strong>?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
              <button onClick={() => { deleteQuiz(quizToDelete.id); setShowQuizDeleteConfirm(false); setQuizToDelete(null); }} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}>Yes</button>
              <button onClick={() => { setShowQuizDeleteConfirm(false); setQuizToDelete(null); }} style={{ backgroundColor: '#6c757d', color: 'white', border: 'none', padding: '0.75rem 1.5rem', borderRadius: '4px', cursor: 'pointer' }}>No</button>
            </div>
          </div>
        </div>
      )}
      
      {showDeleteOrgDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
          <div style={{ backgroundColor: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 8px 16px rgba(0,0,0,0.2)', maxWidth: '500px', width: '90%', border: '2px solid #dc3545' }}>
            {loadingOrganization ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
                <p style={{ margin: '0', color: '#666', fontSize: '1.2rem' }}>Loading organization details...</p>
              </div>
            ) : (
              <>
                <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
                  <h3 style={{ margin: '0 0 1rem 0', color: '#dc3545', fontSize: '1.5rem' }}>Delete {currentOrganization?.type || 'Account'} Account</h3>
                  <p style={{ margin: '0', color: '#666', lineHeight: '1.6' }}>
                    Are you sure you want to delete this {currentOrganization?.type ? currentOrganization.type.toLowerCase() : ''} account? This action cannot be undone and all data will be permanently removed.
                  </p>
                </div>
                
                {deleteOrgMessage && (
                  <div style={{ textAlign: 'center', marginBottom: '1rem', padding: '0.75rem', color: deleteOrgMessage.includes('Error') ? '#dc3545' : '#28a745', backgroundColor: deleteOrgMessage.includes('Error') ? '#f8d7da' : '#d4edda', borderRadius: '6px', border: `1px solid ${deleteOrgMessage.includes('Error') ? '#f5c6cb' : '#c3e6cb'}`, fontWeight: 'bold' }}>
                    {deleteOrgMessage}
                  </div>
                )}
                
                {!deletingOrganization && (
                  <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                    <button 
                      onClick={async () => {
                        setDeletingOrganization(true);
                        try {
                          const response = await fetch(`${API_BASE_URL}/organization/current`, {
                            method: 'DELETE',
                            headers: {
                              'session-id': currentUser?.sessionId
                            }
                          });
                          
                          if (response.ok) {
                            setDeleteOrgMessage(`${currentOrganization?.type || 'Account'} account deleted successfully. Logging out...`);
                            setTimeout(() => {
                              setCurrentUser(null);
                              navigate('/login');
                            }, 2000);
                          } else {
                            const errorText = await response.text();
                            setDeleteOrgMessage(`Error: ${errorText || 'Failed to delete organization'}`);
                            setDeletingOrganization(false);
                          }
                        } catch (error) {
                          setDeleteOrgMessage(`Error: ${error.message}`);
                          setDeletingOrganization(false);
                        }
                      }}
                      style={{ 
                        padding: '0.75rem 1.5rem', 
                        backgroundColor: '#dc3545', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '0.8rem'
                      }}
                    >
                      Yes, Delete this {currentOrganization?.type || 'Organization'} Account
                    </button>
                    <button 
                      onClick={() => {
                        setShowDeleteOrgDialog(false);
                        setDeleteOrgMessage('');
                        setDeletingOrganization(false);
                      }} 
                      style={{ 
                        padding: '0.75rem 1.5rem', 
                        backgroundColor: '#6c757d', 
                        color: 'white', 
                        border: 'none', 
                        borderRadius: '6px', 
                        cursor: 'pointer',
                        fontWeight: 'bold',
                        fontSize: '1rem'
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {showDeleteExpenseDialog && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2100 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', width: '90%', maxWidth: '420px', padding: '1.25rem' }}>
            <h3 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Delete Expense</h3>
            <p style={{ marginTop: 0, marginBottom: '1rem', color: '#495057' }}>
              Are you sure you want to delete this expense?
            </p>
            <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#6c757d' }}>
              {(expenseToDelete?.expense || 'Expense')} {expenseToDelete?.date ? `(${expenseToDelete.date})` : ''}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
              <button
                onClick={() => {
                  setShowDeleteExpenseDialog(false);
                  setExpenseToDelete(null);
                }}
                style={{ padding: '0.5rem 0.9rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteExpense}
                style={{ padding: '0.5rem 0.9rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {showFeatureRequestDialog && selectedFeatureRequest && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2200 }}>
          <div style={{ backgroundColor: 'white', width: '900px', maxWidth: '95vw', maxHeight: '90vh', borderRadius: '8px', boxShadow: '0 6px 24px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', backgroundColor: '#e6f7ff', borderBottom: '1px solid #ccc', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '1rem 1.25rem' }}>
              <h3 style={{ margin: 0, textAlign: 'center' }}>Feature Request</h3>
              <button onClick={closeFeatureRequestDialog} style={{ position: 'absolute', top: '0.5rem', right: '0.6rem', background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem', overflow: 'auto' }}>
              <div style={{ marginBottom: '0.5rem' }}><strong>Title:</strong> {selectedFeatureRequest.title || ''}</div>
              <div style={{ marginBottom: '0.5rem' }}><strong>Description:</strong> {selectedFeatureRequest.description || ''}</div>
              {(currentUser?.user?.orgRole === 'superuser' || currentUser?.user?.orgRole === 'admin') ? (
                <>
                  <div style={{ marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ minWidth: '70px' }}>Priority:</strong>
                    <select value={selectedFeatureRequest.priority || 'medium'} onChange={(e) => updateSelectedFeatureRequestField('priority', e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '140px' }}>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="highest">Highest</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <strong style={{ minWidth: '70px' }}>Status:</strong>
                    <select value={selectedFeatureRequest.status || 'open'} onChange={(e) => updateSelectedFeatureRequestField('status', e.target.value)} style={{ padding: '0.35rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '170px' }}>
                      <option value="open">Open</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="rejected">Rejected</option>
                      <option value="duplicate">Duplicate</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                    <button onClick={saveSelectedFeatureRequest} style={{ padding: '0.45rem 0.8rem', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Changes</button>
                  </div>
                </>
              ) : (
                <>
                  <div style={{ marginBottom: '0.5rem' }}><strong>Priority:</strong> {selectedFeatureRequest.priority || ''}</div>
                  <div style={{ marginBottom: '1rem' }}><strong>Status:</strong> {selectedFeatureRequest.status || ''}</div>
                </>
              )}

              <div style={{ borderTop: '1px solid #dee2e6', paddingTop: '1rem' }}>
                <h4 style={{ marginTop: 0, marginBottom: '0.75rem' }}>Comments</h4>
                {featureRequestCommentMessage && (
                  <div style={{ padding: '0.6rem', marginBottom: '0.75rem', borderRadius: '4px', backgroundColor: featureRequestCommentMessageType === 'success' ? '#d4edda' : '#f8d7da', color: featureRequestCommentMessageType === 'success' ? '#155724' : '#721c24', border: `1px solid ${featureRequestCommentMessageType === 'success' ? '#c3e6cb' : '#f5c6cb'}` }}>
                    {featureRequestCommentMessage}
                  </div>
                )}

                <div style={{ marginBottom: '0.75rem' }}>
                  <textarea
                    value={featureRequestCommentInput}
                    onChange={(e) => setFeatureRequestCommentInput(e.target.value)}
                    placeholder="Add a comment..."
                    rows="3"
                    style={{ width: '100%', padding: '0.65rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                    <div>
                      <input ref={featureRequestCommentFileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={handleFeatureRequestCommentFileChange} />
                      <span style={{ marginLeft: '0.5rem', color: '#6c757d', fontSize: '0.8rem' }}>PDF/JPEG/PNG, max 1 MB</span>
                      {featureRequestCommentFile && <div style={{ color: '#6c757d', fontSize: '0.8rem' }}>Selected: {featureRequestCommentFile.name}</div>}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
                      <button onClick={saveFeatureRequestComment} style={{ padding: '0.45rem 0.8rem', backgroundColor: '#007bff', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Save Comment</button>
                    </div>
                  </div>
                </div>

                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                  {loadingFeatureRequestComments ? (
                    <div style={{ padding: '0.9rem', color: '#6c757d' }}>Loading comments...</div>
                  ) : featureRequestComments.length === 0 ? (
                    <div style={{ padding: '0.9rem', color: '#6c757d' }}>No comments yet</div>
                  ) : (
                    featureRequestComments
                      .slice()
                      .sort((a, b) => {
                        const aTime = parseUtcCommentDatetime(a.commentDatetime)?.getTime() || 0;
                        const bTime = parseUtcCommentDatetime(b.commentDatetime)?.getTime() || 0;
                        return aTime - bTime;
                      })
                      .map((commentItem) => (
                        <div key={commentItem.id} style={{ padding: '0.75rem', borderBottom: '1px solid #e5e5e5', backgroundColor: 'white' }}>
                          <div style={{ fontSize: '0.82rem', color: '#6c757d', marginBottom: '0.3rem' }}>
                            {commentItem.userName || `User ${commentItem.userId || ''}`} • {formatCommentDatetimeLocal(commentItem.commentDatetime)}
                          </div>
                          <div style={{ whiteSpace: 'pre-wrap' }}>{commentItem.comment || ''}</div>
                          {hasFeatureRequestCommentAttachment(commentItem) && (() => {
                            const attachmentUrl = getFeatureRequestCommentAttachmentUrl(commentItem);
                            return attachmentUrl ? (
                              <button type="button" onClick={() => setSelectedCommentAttachmentUrl(resolveFeatureRequestCommentAttachmentUrl(attachmentUrl, API_BASE_URL))} style={{ display: 'inline-block', marginTop: '0.4rem', padding: 0, border: 'none', background: 'none', color: '#007bff', cursor: 'pointer' }}>
                                📎 View attachment
                              </button>
                            ) : (
                              <span title="Attachment available" style={{ display: 'inline-block', marginTop: '0.4rem' }}>📎 Attachment</span>
                            );
                          })()}
                        </div>
                      ))
                  )}
                </div>
              </div>
            </div>
            <div style={{ textAlign: 'center', padding: '0.9rem', borderTop: '1px solid #ccc', backgroundColor: '#f8f9fa', borderBottomLeftRadius: '8px', borderBottomRightRadius: '8px' }}>
              <button onClick={closeFeatureRequestDialog} style={{ padding: '0.5rem 1rem', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        </div>
      )}
      <FeatureRequestAttachmentViewer url={selectedCommentAttachmentUrl} onClose={() => setSelectedCommentAttachmentUrl('')} />
    </>
  );
};

export default Manage;